/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const rt = (i) => (t) => typeof t == "function" ? ((e, s) => (customElements.define(e, s), s))(i, t) : ((e, s) => {
  const { kind: o, elements: n } = s;
  return { kind: o, elements: n, finisher(r) {
    customElements.define(e, r);
  } };
})(i, t);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const $i = (i, t) => t.kind === "method" && t.descriptor && !("value" in t.descriptor) ? { ...t, finisher(e) {
  e.createProperty(t.key, i);
} } : { kind: "field", key: Symbol(), placement: "own", descriptor: {}, originalKey: t.key, initializer() {
  typeof t.initializer == "function" && (this[t.key] = t.initializer.call(this));
}, finisher(e) {
  e.createProperty(t.key, i);
} }, ki = (i, t, e) => {
  t.constructor.createProperty(e, i);
};
function $(i) {
  return (t, e) => e !== void 0 ? ki(i, t, e) : $i(i, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function x(i) {
  return $({ ...i, state: !0 });
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Zt;
((Zt = window.HTMLSlotElement) === null || Zt === void 0 ? void 0 : Zt.prototype.assignedElements) != null;
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Et = window, Ce = Et.ShadowRoot && (Et.ShadyCSS === void 0 || Et.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, ye = Symbol(), Le = /* @__PURE__ */ new WeakMap();
let ii = class {
  constructor(t, e, s) {
    if (this._$cssResult$ = !0, s !== ye)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (Ce && t === void 0) {
      const s = e !== void 0 && e.length === 1;
      s && (t = Le.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), s && Le.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const _i = (i) => new ii(typeof i == "string" ? i : i + "", void 0, ye), at = (i, ...t) => {
  const e = i.length === 1 ? i[0] : t.reduce((s, o, n) => s + ((r) => {
    if (r._$cssResult$ === !0)
      return r.cssText;
    if (typeof r == "number")
      return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(o) + i[n + 1], i[0]);
  return new ii(e, i, ye);
}, Ei = (i, t) => {
  Ce ? i.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet) : t.forEach((e) => {
    const s = document.createElement("style"), o = Et.litNonce;
    o !== void 0 && s.setAttribute("nonce", o), s.textContent = e.cssText, i.appendChild(s);
  });
}, Me = Ce ? (i) => i : (i) => i instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const s of t.cssRules)
    e += s.cssText;
  return _i(e);
})(i) : i;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Gt;
const Lt = window, Ve = Lt.trustedTypes, Si = Ve ? Ve.emptyScript : "", Re = Lt.reactiveElementPolyfillSupport, re = { toAttribute(i, t) {
  switch (t) {
    case Boolean:
      i = i ? Si : null;
      break;
    case Object:
    case Array:
      i = i == null ? i : JSON.stringify(i);
  }
  return i;
}, fromAttribute(i, t) {
  let e = i;
  switch (t) {
    case Boolean:
      e = i !== null;
      break;
    case Number:
      e = i === null ? null : Number(i);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(i);
      } catch {
        e = null;
      }
  }
  return e;
} }, si = (i, t) => t !== i && (t == t || i == i), zt = { attribute: !0, type: String, converter: re, reflect: !1, hasChanged: si }, ae = "finalized";
let J = class extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this._$Eu();
  }
  static addInitializer(t) {
    var e;
    this.finalize(), ((e = this.h) !== null && e !== void 0 ? e : this.h = []).push(t);
  }
  static get observedAttributes() {
    this.finalize();
    const t = [];
    return this.elementProperties.forEach((e, s) => {
      const o = this._$Ep(s, e);
      o !== void 0 && (this._$Ev.set(o, s), t.push(o));
    }), t;
  }
  static createProperty(t, e = zt) {
    if (e.state && (e.attribute = !1), this.finalize(), this.elementProperties.set(t, e), !e.noAccessor && !this.prototype.hasOwnProperty(t)) {
      const s = typeof t == "symbol" ? Symbol() : "__" + t, o = this.getPropertyDescriptor(t, s, e);
      o !== void 0 && Object.defineProperty(this.prototype, t, o);
    }
  }
  static getPropertyDescriptor(t, e, s) {
    return { get() {
      return this[e];
    }, set(o) {
      const n = this[t];
      this[e] = o, this.requestUpdate(t, n, s);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) || zt;
  }
  static finalize() {
    if (this.hasOwnProperty(ae))
      return !1;
    this[ae] = !0;
    const t = Object.getPrototypeOf(this);
    if (t.finalize(), t.h !== void 0 && (this.h = [...t.h]), this.elementProperties = new Map(t.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const e = this.properties, s = [...Object.getOwnPropertyNames(e), ...Object.getOwnPropertySymbols(e)];
      for (const o of s)
        this.createProperty(o, e[o]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const s = new Set(t.flat(1 / 0).reverse());
      for (const o of s)
        e.unshift(Me(o));
    } else
      t !== void 0 && e.push(Me(t));
    return e;
  }
  static _$Ep(t, e) {
    const s = e.attribute;
    return s === !1 ? void 0 : typeof s == "string" ? s : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  _$Eu() {
    var t;
    this._$E_ = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (t = this.constructor.h) === null || t === void 0 || t.forEach((e) => e(this));
  }
  addController(t) {
    var e, s;
    ((e = this._$ES) !== null && e !== void 0 ? e : this._$ES = []).push(t), this.renderRoot !== void 0 && this.isConnected && ((s = t.hostConnected) === null || s === void 0 || s.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$ES) === null || e === void 0 || e.splice(this._$ES.indexOf(t) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((t, e) => {
      this.hasOwnProperty(e) && (this._$Ei.set(e, this[e]), delete this[e]);
    });
  }
  createRenderRoot() {
    var t;
    const e = (t = this.shadowRoot) !== null && t !== void 0 ? t : this.attachShadow(this.constructor.shadowRootOptions);
    return Ei(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var t;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$ES) === null || t === void 0 || t.forEach((e) => {
      var s;
      return (s = e.hostConnected) === null || s === void 0 ? void 0 : s.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$ES) === null || t === void 0 || t.forEach((e) => {
      var s;
      return (s = e.hostDisconnected) === null || s === void 0 ? void 0 : s.call(e);
    });
  }
  attributeChangedCallback(t, e, s) {
    this._$AK(t, s);
  }
  _$EO(t, e, s = zt) {
    var o;
    const n = this.constructor._$Ep(t, s);
    if (n !== void 0 && s.reflect === !0) {
      const r = (((o = s.converter) === null || o === void 0 ? void 0 : o.toAttribute) !== void 0 ? s.converter : re).toAttribute(e, s.type);
      this._$El = t, r == null ? this.removeAttribute(n) : this.setAttribute(n, r), this._$El = null;
    }
  }
  _$AK(t, e) {
    var s;
    const o = this.constructor, n = o._$Ev.get(t);
    if (n !== void 0 && this._$El !== n) {
      const r = o.getPropertyOptions(n), d = typeof r.converter == "function" ? { fromAttribute: r.converter } : ((s = r.converter) === null || s === void 0 ? void 0 : s.fromAttribute) !== void 0 ? r.converter : re;
      this._$El = n, this[n] = d.fromAttribute(e, r.type), this._$El = null;
    }
  }
  requestUpdate(t, e, s) {
    let o = !0;
    t !== void 0 && (((s = s || this.constructor.getPropertyOptions(t)).hasChanged || si)(this[t], e) ? (this._$AL.has(t) || this._$AL.set(t, e), s.reflect === !0 && this._$El !== t && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(t, s))) : o = !1), !this.isUpdatePending && o && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var t;
    if (!this.isUpdatePending)
      return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((o, n) => this[n] = o), this._$Ei = void 0);
    let e = !1;
    const s = this._$AL;
    try {
      e = this.shouldUpdate(s), e ? (this.willUpdate(s), (t = this._$ES) === null || t === void 0 || t.forEach((o) => {
        var n;
        return (n = o.hostUpdate) === null || n === void 0 ? void 0 : n.call(o);
      }), this.update(s)) : this._$Ek();
    } catch (o) {
      throw e = !1, this._$Ek(), o;
    }
    e && this._$AE(s);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$ES) === null || e === void 0 || e.forEach((s) => {
      var o;
      return (o = s.hostUpdated) === null || o === void 0 ? void 0 : o.call(s);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$EC !== void 0 && (this._$EC.forEach((e, s) => this._$EO(s, this[s], e)), this._$EC = void 0), this._$Ek();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
J[ae] = !0, J.elementProperties = /* @__PURE__ */ new Map(), J.elementStyles = [], J.shadowRootOptions = { mode: "open" }, Re == null || Re({ ReactiveElement: J }), ((Gt = Lt.reactiveElementVersions) !== null && Gt !== void 0 ? Gt : Lt.reactiveElementVersions = []).push("1.6.3");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var qt;
const Mt = window, tt = Mt.trustedTypes, Oe = tt ? tt.createPolicy("lit-html", { createHTML: (i) => i }) : void 0, le = "$lit$", N = `lit$${(Math.random() + "").slice(9)}$`, oi = "?" + N, Li = `<${oi}>`, F = document, vt = () => F.createComment(""), mt = (i) => i === null || typeof i != "object" && typeof i != "function", ni = Array.isArray, Mi = (i) => ni(i) || typeof (i == null ? void 0 : i[Symbol.iterator]) == "function", Ft = `[ 	
\f\r]`, dt = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, He = /-->/g, Ue = />/g, Z = RegExp(`>|${Ft}(?:([^\\s"'>=/]+)(${Ft}*=${Ft}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), De = /'/g, Te = /"/g, ri = /^(?:script|style|textarea|title)$/i, Vi = (i) => (t, ...e) => ({ _$litType$: i, strings: t, values: e }), g = Vi(1), X = Symbol.for("lit-noChange"), f = Symbol.for("lit-nothing"), Ne = /* @__PURE__ */ new WeakMap(), z = F.createTreeWalker(F, 129, null, !1);
function ai(i, t) {
  if (!Array.isArray(i) || !i.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return Oe !== void 0 ? Oe.createHTML(t) : t;
}
const Ri = (i, t) => {
  const e = i.length - 1, s = [];
  let o, n = t === 2 ? "<svg>" : "", r = dt;
  for (let d = 0; d < e; d++) {
    const a = i[d];
    let l, p, h = -1, c = 0;
    for (; c < a.length && (r.lastIndex = c, p = r.exec(a), p !== null); )
      c = r.lastIndex, r === dt ? p[1] === "!--" ? r = He : p[1] !== void 0 ? r = Ue : p[2] !== void 0 ? (ri.test(p[2]) && (o = RegExp("</" + p[2], "g")), r = Z) : p[3] !== void 0 && (r = Z) : r === Z ? p[0] === ">" ? (r = o ?? dt, h = -1) : p[1] === void 0 ? h = -2 : (h = r.lastIndex - p[2].length, l = p[1], r = p[3] === void 0 ? Z : p[3] === '"' ? Te : De) : r === Te || r === De ? r = Z : r === He || r === Ue ? r = dt : (r = Z, o = void 0);
    const v = r === Z && i[d + 1].startsWith("/>") ? " " : "";
    n += r === dt ? a + Li : h >= 0 ? (s.push(l), a.slice(0, h) + le + a.slice(h) + N + v) : a + N + (h === -2 ? (s.push(void 0), d) : v);
  }
  return [ai(i, n + (i[e] || "<?>") + (t === 2 ? "</svg>" : "")), s];
};
let de = class li {
  constructor({ strings: t, _$litType$: e }, s) {
    let o;
    this.parts = [];
    let n = 0, r = 0;
    const d = t.length - 1, a = this.parts, [l, p] = Ri(t, e);
    if (this.el = li.createElement(l, s), z.currentNode = this.el.content, e === 2) {
      const h = this.el.content, c = h.firstChild;
      c.remove(), h.append(...c.childNodes);
    }
    for (; (o = z.nextNode()) !== null && a.length < d; ) {
      if (o.nodeType === 1) {
        if (o.hasAttributes()) {
          const h = [];
          for (const c of o.getAttributeNames())
            if (c.endsWith(le) || c.startsWith(N)) {
              const v = p[r++];
              if (h.push(c), v !== void 0) {
                const U = o.getAttribute(v.toLowerCase() + le).split(N), C = /([.?@])?(.*)/.exec(v);
                a.push({ type: 1, index: n, name: C[2], strings: U, ctor: C[1] === "." ? Hi : C[1] === "?" ? Di : C[1] === "@" ? Ti : It });
              } else
                a.push({ type: 6, index: n });
            }
          for (const c of h)
            o.removeAttribute(c);
        }
        if (ri.test(o.tagName)) {
          const h = o.textContent.split(N), c = h.length - 1;
          if (c > 0) {
            o.textContent = tt ? tt.emptyScript : "";
            for (let v = 0; v < c; v++)
              o.append(h[v], vt()), z.nextNode(), a.push({ type: 2, index: ++n });
            o.append(h[c], vt());
          }
        }
      } else if (o.nodeType === 8)
        if (o.data === oi)
          a.push({ type: 2, index: n });
        else {
          let h = -1;
          for (; (h = o.data.indexOf(N, h + 1)) !== -1; )
            a.push({ type: 7, index: n }), h += N.length - 1;
        }
      n++;
    }
  }
  static createElement(t, e) {
    const s = F.createElement("template");
    return s.innerHTML = t, s;
  }
};
function et(i, t, e = i, s) {
  var o, n, r, d;
  if (t === X)
    return t;
  let a = s !== void 0 ? (o = e._$Co) === null || o === void 0 ? void 0 : o[s] : e._$Cl;
  const l = mt(t) ? void 0 : t._$litDirective$;
  return (a == null ? void 0 : a.constructor) !== l && ((n = a == null ? void 0 : a._$AO) === null || n === void 0 || n.call(a, !1), l === void 0 ? a = void 0 : (a = new l(i), a._$AT(i, e, s)), s !== void 0 ? ((r = (d = e)._$Co) !== null && r !== void 0 ? r : d._$Co = [])[s] = a : e._$Cl = a), a !== void 0 && (t = et(i, a._$AS(i, t.values), a, s)), t;
}
let Oi = class {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    var e;
    const { el: { content: s }, parts: o } = this._$AD, n = ((e = t == null ? void 0 : t.creationScope) !== null && e !== void 0 ? e : F).importNode(s, !0);
    z.currentNode = n;
    let r = z.nextNode(), d = 0, a = 0, l = o[0];
    for (; l !== void 0; ) {
      if (d === l.index) {
        let p;
        l.type === 2 ? p = new xe(r, r.nextSibling, this, t) : l.type === 1 ? p = new l.ctor(r, l.name, l.strings, this, t) : l.type === 6 && (p = new Ni(r, this, t)), this._$AV.push(p), l = o[++a];
      }
      d !== (l == null ? void 0 : l.index) && (r = z.nextNode(), d++);
    }
    return z.currentNode = F, n;
  }
  v(t) {
    let e = 0;
    for (const s of this._$AV)
      s !== void 0 && (s.strings !== void 0 ? (s._$AI(t, s, e), e += s.strings.length - 2) : s._$AI(t[e])), e++;
  }
}, xe = class di {
  constructor(t, e, s, o) {
    var n;
    this.type = 2, this._$AH = f, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = s, this.options = o, this._$Cp = (n = o == null ? void 0 : o.isConnected) === null || n === void 0 || n;
  }
  get _$AU() {
    var t, e;
    return (e = (t = this._$AM) === null || t === void 0 ? void 0 : t._$AU) !== null && e !== void 0 ? e : this._$Cp;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = et(this, t, e), mt(t) ? t === f || t == null || t === "" ? (this._$AH !== f && this._$AR(), this._$AH = f) : t !== this._$AH && t !== X && this._(t) : t._$litType$ !== void 0 ? this.g(t) : t.nodeType !== void 0 ? this.$(t) : Mi(t) ? this.T(t) : this._(t);
  }
  k(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  $(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.k(t));
  }
  _(t) {
    this._$AH !== f && mt(this._$AH) ? this._$AA.nextSibling.data = t : this.$(F.createTextNode(t)), this._$AH = t;
  }
  g(t) {
    var e;
    const { values: s, _$litType$: o } = t, n = typeof o == "number" ? this._$AC(t) : (o.el === void 0 && (o.el = de.createElement(ai(o.h, o.h[0]), this.options)), o);
    if (((e = this._$AH) === null || e === void 0 ? void 0 : e._$AD) === n)
      this._$AH.v(s);
    else {
      const r = new Oi(n, this), d = r.u(this.options);
      r.v(s), this.$(d), this._$AH = r;
    }
  }
  _$AC(t) {
    let e = Ne.get(t.strings);
    return e === void 0 && Ne.set(t.strings, e = new de(t)), e;
  }
  T(t) {
    ni(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let s, o = 0;
    for (const n of t)
      o === e.length ? e.push(s = new di(this.k(vt()), this.k(vt()), this, this.options)) : s = e[o], s._$AI(n), o++;
    o < e.length && (this._$AR(s && s._$AB.nextSibling, o), e.length = o);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var s;
    for ((s = this._$AP) === null || s === void 0 || s.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const o = t.nextSibling;
      t.remove(), t = o;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this._$Cp = t, (e = this._$AP) === null || e === void 0 || e.call(this, t));
  }
};
class It {
  constructor(t, e, s, o, n) {
    this.type = 1, this._$AH = f, this._$AN = void 0, this.element = t, this.name = e, this._$AM = o, this.options = n, s.length > 2 || s[0] !== "" || s[1] !== "" ? (this._$AH = Array(s.length - 1).fill(new String()), this.strings = s) : this._$AH = f;
  }
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t, e = this, s, o) {
    const n = this.strings;
    let r = !1;
    if (n === void 0)
      t = et(this, t, e, 0), r = !mt(t) || t !== this._$AH && t !== X, r && (this._$AH = t);
    else {
      const d = t;
      let a, l;
      for (t = n[0], a = 0; a < n.length - 1; a++)
        l = et(this, d[s + a], e, a), l === X && (l = this._$AH[a]), r || (r = !mt(l) || l !== this._$AH[a]), l === f ? t = f : t !== f && (t += (l ?? "") + n[a + 1]), this._$AH[a] = l;
    }
    r && !o && this.j(t);
  }
  j(t) {
    t === f ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
let Hi = class extends It {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === f ? void 0 : t;
  }
};
const Ui = tt ? tt.emptyScript : "";
let Di = class extends It {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    t && t !== f ? this.element.setAttribute(this.name, Ui) : this.element.removeAttribute(this.name);
  }
}, Ti = class extends It {
  constructor(t, e, s, o, n) {
    super(t, e, s, o, n), this.type = 5;
  }
  _$AI(t, e = this) {
    var s;
    if ((t = (s = et(this, t, e, 0)) !== null && s !== void 0 ? s : f) === X)
      return;
    const o = this._$AH, n = t === f && o !== f || t.capture !== o.capture || t.once !== o.once || t.passive !== o.passive, r = t !== f && (o === f || n);
    n && this.element.removeEventListener(this.name, this, o), r && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e, s;
    typeof this._$AH == "function" ? this._$AH.call((s = (e = this.options) === null || e === void 0 ? void 0 : e.host) !== null && s !== void 0 ? s : this.element, t) : this._$AH.handleEvent(t);
  }
}, Ni = class {
  constructor(t, e, s) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = s;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    et(this, t);
  }
};
const Ie = Mt.litHtmlPolyfillSupport;
Ie == null || Ie(de, xe), ((qt = Mt.litHtmlVersions) !== null && qt !== void 0 ? qt : Mt.litHtmlVersions = []).push("2.8.0");
const Ii = (i, t, e) => {
  var s, o;
  const n = (s = e == null ? void 0 : e.renderBefore) !== null && s !== void 0 ? s : t;
  let r = n._$litPart$;
  if (r === void 0) {
    const d = (o = e == null ? void 0 : e.renderBefore) !== null && o !== void 0 ? o : null;
    n._$litPart$ = r = new xe(t.insertBefore(vt(), d), d, void 0, e ?? {});
  }
  return r._$AI(i), r;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Xt, Qt;
let R = class extends J {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t, e;
    const s = super.createRenderRoot();
    return (t = (e = this.renderOptions).renderBefore) !== null && t !== void 0 || (e.renderBefore = s.firstChild), s;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Ii(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) === null || t === void 0 || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) === null || t === void 0 || t.setConnected(!1);
  }
  render() {
    return X;
  }
};
R.finalized = !0, R._$litElement$ = !0, (Xt = globalThis.litElementHydrateSupport) === null || Xt === void 0 || Xt.call(globalThis, { LitElement: R });
const Pe = globalThis.litElementPolyfillSupport;
Pe == null || Pe({ LitElement: R });
((Qt = globalThis.litElementVersions) !== null && Qt !== void 0 ? Qt : globalThis.litElementVersions = []).push("3.3.3");
var Pi = Object.defineProperty, ji = Object.getOwnPropertyDescriptor, Bi = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? ji(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Pi(t, e, o), o;
};
let he = class extends R {
  render() {
    return g`
            <div id="loading"></div>
        `;
  }
};
he.styles = at`
        #loading {
          display: inline-block;
          width: 25px;
          height: 25px;
          border: 3px solid rgba(255,255,255,.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
        }
        
        @keyframes spin {
          to {
            transform: rotate(360deg); 
          }
        }
        @-webkit-keyframes spin {
          to { 
            transform: rotate(360deg); 
          }
        }
    `;
he = Bi([
  rt("videochat-loading")
], he);
var Zi = Object.defineProperty, Gi = Object.getOwnPropertyDescriptor, zi = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Gi(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Zi(t, e, o), o;
};
let ce = class extends R {
  onLeave() {
    this.dispatchEvent(new CustomEvent("leave"));
  }
  render() {
    return g`
            <div id="wrapper">
                <button id="button" @click=${this.onLeave.bind(this)}>
                    <span>Leave</span>
                </button>
                <div id="loading">
                    <videochat-loading></videochat-loading>
                </div>
            </div>
            <audio src="https://storage.googleapis.com/maia-videochat/ringing.wav" loop autoplay></audio>
        `;
  }
};
ce.styles = at`
      :host {
        box-sizing: border-box;  
      }
      
      button {
        all: unset;
        cursor: pointer;
      }
      
      #wrapper {
        background-color: #F2545B;
        display: flex;
        height: 55px;
        width: 200px;
        border-radius: 4px;
        align-items: center;
        color: white;
        font-size: 24px;
      }
      
      #button {
        width: 80%;
        height: 100%;
        border-right: 1px solid white;
        display: flex;
        justify-content: center;
        align-items: center;
        
        &:hover {
          background-color: #EE1B26;
          border-bottom-left-radius: 4px;
          border-top-left-radius: 4px;
        }
      }
      
      #loading {
        width: 20%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `;
ce = zi([
  rt("videochat-calling")
], ce);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const hi = { ATTRIBUTE: 1, CHILD: 2, PROPERTY: 3, BOOLEAN_ATTRIBUTE: 4, EVENT: 5, ELEMENT: 6 }, ci = (i) => (...t) => ({ _$litDirective$: i, values: t });
class pi {
  constructor(t) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(t, e, s) {
    this._$Ct = t, this._$AM = e, this._$Ci = s;
  }
  _$AS(t, e) {
    return this.update(t, e);
  }
  update(t, e) {
    return this.render(...e);
  }
}
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const k = ci(class extends pi {
  constructor(i) {
    var t;
    if (super(i), i.type !== hi.ATTRIBUTE || i.name !== "class" || ((t = i.strings) === null || t === void 0 ? void 0 : t.length) > 2)
      throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(i) {
    return " " + Object.keys(i).filter((t) => i[t]).join(" ") + " ";
  }
  update(i, [t]) {
    var e, s;
    if (this.it === void 0) {
      this.it = /* @__PURE__ */ new Set(), i.strings !== void 0 && (this.nt = new Set(i.strings.join(" ").split(/\s/).filter((n) => n !== "")));
      for (const n in t)
        t[n] && !(!((e = this.nt) === null || e === void 0) && e.has(n)) && this.it.add(n);
      return this.render(t);
    }
    const o = i.element.classList;
    this.it.forEach((n) => {
      n in t || (o.remove(n), this.it.delete(n));
    });
    for (const n in t) {
      const r = !!t[n];
      r === this.it.has(n) || !((s = this.nt) === null || s === void 0) && s.has(n) || (r ? (o.add(n), this.it.add(n)) : (o.remove(n), this.it.delete(n)));
    }
    return X;
  }
});
var qi = Object.defineProperty, Fi = Object.getOwnPropertyDescriptor, ui = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Fi(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && qi(t, e, o), o;
};
let Vt = class extends R {
  constructor() {
    super(...arguments), this.moreOpen = !1;
  }
  connectedCallback() {
    super.connectedCallback(), document.addEventListener("click", (i) => {
      this.moreOpen && i.target.nodeName !== "MAIA-VIDEOCHAT" && i.target.nodeName !== "MAIA-VIDEOCHAT-START" && (this.moreOpen = !1);
    });
  }
  toggleMore() {
    this.moreOpen = !this.moreOpen;
  }
  handleCall() {
    this.dispatchEvent(new CustomEvent("call", {
      detail: {
        presentation: !1
      }
    }));
  }
  handleCallWho() {
    this.dispatchEvent(new CustomEvent("call", {
      detail: {
        presentation: !0
      }
    }));
  }
  render() {
    return g`
            <div id="call-wrapper">
                <button id="call-btn" @click=${this.handleCall.bind(this)}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                       <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                       <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                       <g id="SVGRepo_iconCarrier">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M17.3545 22.2323C15.3344 21.7262 11.1989 20.2993 7.44976 16.5502C3.70065 12.8011 2.2738 8.66559 1.76767 6.6455C1.47681 5.48459 2.00058 4.36434 2.88869 3.72997L5.21694 2.06693C6.57922 1.09388 8.47432 1.42407 9.42724 2.80051L10.893 4.91776C11.5152 5.8165 11.3006 7.0483 10.4111 7.68365L9.24234 8.51849C9.41923 9.1951 9.96939 10.5846 11.6924 12.3076C13.4154 14.0306 14.8049 14.5807 15.4815 14.7576L16.3163 13.5888C16.9517 12.6994 18.1835 12.4847 19.0822 13.1069L21.1995 14.5727C22.5759 15.5257 22.9061 17.4207 21.933 18.783L20.27 21.1113C19.6356 21.9994 18.5154 22.5232 17.3545 22.2323ZM8.86397 15.136C12.2734 18.5454 16.0358 19.8401 17.8405 20.2923C18.1043 20.3583 18.4232 20.2558 18.6425 19.9488L20.3056 17.6205C20.6299 17.1665 20.5199 16.5348 20.061 16.2171L17.9438 14.7513L17.0479 16.0056C16.6818 16.5182 16.0047 16.9202 15.2163 16.7501C14.2323 16.5378 12.4133 15.8569 10.2782 13.7218C8.1431 11.5867 7.46219 9.7677 7.24987 8.7837C7.07977 7.9953 7.48181 7.31821 7.99439 6.95208L9.24864 6.05618L7.78285 3.93893C7.46521 3.48011 6.83351 3.37005 6.37942 3.6944L4.05117 5.35744C3.74413 5.57675 3.64162 5.89565 3.70771 6.15943C4.15989 7.96418 5.45459 11.7266 8.86397 15.136Z" fill="#ffffff"></path>
                       </g>
                    </svg>
                </button>
                <button id="more-btn" @click=${this.toggleMore.bind(this)}>
                    ${this.moreOpen ? g`
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                <g id="SVGRepo_iconCarrier">
                                    <g id="Arrow / Caret_Up_SM">
                                        <path id="Vector" d="M9 13L12 10L15 13" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                                    </g>
                                </g>
                            </svg>
                            ` : g`
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                <g id="SVGRepo_iconCarrier">
                                    <g id="Arrow / Caret_Down_MD">
                                        <path id="Vector" d="M16 10L12 14L8 10" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                                    </g>
                                </g>
                            </svg>           
                            `}
                </button>
            </div>
            ${this.moreOpen ? g`
                    <ul id="more-list" class=${k({ open: this.moreOpen })}>
                        <li class="more-list-item">
                            <button  id="more-list-btn-call" class="more-list-btn" @click=${this.handleCall.bind(this)}>
                                <svg height="200px" width="200px" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xml:space="preserve" fill="#000000">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        <style type="text/css"> .st0{fill:#000000;} </style>
                                        <g>
                                            <path class="st0" d="M337.168,289.578c-5.129-5.133-13.457-5.133-18.598,0l-16.031,16.038c-4.68,4.68-12.122,5.149-17.352,1.102 c0,0-15.332-10.109-40.402-35.179c-25.07-25.07-35.175-40.414-35.175-40.414c-4.055-5.226-3.578-12.656,1.102-17.343l16.031-16.031 c5.141-5.134,5.141-13.462,0-18.594l-35.34-35.343c-5.133-5.133-13.453-5.133-18.594,0c-0.122,0.125-1.906,1.906-21.309,21.32 c-22.602,22.594,7.293,91.82,57.574,142.118c50.289,50.281,119.527,80.164,142.121,57.578 c19.394-19.406,21.184-21.203,21.305-21.32c5.141-5.133,5.141-13.461,0-18.586L337.168,289.578z"></path>
                                            <path class="st0" d="M256,0C114.614,0,0,114.617,0,256s114.614,256,256,256s256-114.617,256-256S397.386,0,256,0z M256,472 c-119.102,0-216-96.898-216-216S136.898,40,256,40s216,96.898,216,216S375.102,472,256,472z"></path>
                                        </g>
                                    </g>
                                </svg>
                                <span>Ask MAIA</span>
                            </button>
                        </li>
                        <li class="more-list-item">
                            <button id="more-list-btn-who" class="more-list-btn" @click=${this.handleCallWho.bind(this)}>
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        <circle cx="12" cy="12" r="10" stroke="#000000" stroke-width="1.5"></circle>
                                        <path
                                            d="M10.125 8.875C10.125 7.83947 10.9645 7 12 7C13.0355 7 13.875 7.83947 13.875 8.875C13.875 9.56245 13.505 10.1635 12.9534 10.4899C12.478 10.7711 12 11.1977 12 11.75V13"
                                            stroke="#000000"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                        ></path>
                                        <circle cx="12" cy="16" r="1" fill="#000000"></circle>
                                    </g>
                                </svg>
                                <span>Who is MAIA?</span>
                            </button>
                        </li>
                    </ul>
                ` : null}
        `;
  }
};
Vt.styles = at`
      :host {
        position: relative;
        box-sizing: border-box;
      }
      
      button {
        all: unset;
        cursor: pointer;
      }
      
      ul {
        all: unset;
      }
      
      li {
        all: unset;
      }
      
      #call-wrapper {
        background-color: #80ED99;
        display: flex;
        color: white;
        font-size: 24px;
        border-radius: 4px;
        height: 55px;
        width: 200px;
      }
      
      #call-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 80%;
        border-right: 1px solid white;
        
        gap: 10px;

        & svg {
          width: 40px;
          height: 40px;
        }
        
        &:hover {
          background-color: #3BE362;
          border-bottom-left-radius: 4px;
          border-top-left-radius: 4px;
        }
      }
      
      #more-btn {
        display: flex;
        align-items: center;
        width: 20%;
        
      
        & svg {
          width: 45px;
          height: 45px;
        } 
        
        &:hover {
          background-color: #3BE362;
          border-bottom-right-radius: 4px;
          border-top-right-radius: 4px;
        }
      }
      
      #more-list {
        position: absolute;
        flex-direction: column;
        z-index: 10;
        background-color: white;
        box-shadow: 0 20px 17px -1px rgba(0,0,0,0.1);
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        display: flex;
        width: 200px;
      }
      
      .more-list-item {
        display: flex;
        width: 100%;
        align-items: center;
      }
      
      .more-list-btn {
        padding: 16px 12px;
        display: flex;
        width: 100%;
        align-items: center;
        gap: 10px;
        
        
        & svg {
          width: 20px;
          height: 20px;
        }
        
        &:hover {
          background-color: #BAC7C9;
        }
      }
      
      #more-list-btn-who {
        gap: 8px;
        
        & svg {
          width: 22px;
          height: 22px;
        }
      }
    `;
ui([
  x()
], Vt.prototype, "moreOpen", 2);
Vt = ui([
  rt("maia-videochat-start")
], Vt);
function Xi(i) {
  return i && i.__esModule && Object.prototype.hasOwnProperty.call(i, "default") ? i.default : i;
}
var Qi = Rt;
function Rt() {
}
Rt.mixin = function(i) {
  var t = i.prototype || i;
  t.isWildEmitter = !0, t.on = function(e, s, o) {
    this.callbacks = this.callbacks || {};
    var n = arguments.length === 3, r = n ? arguments[1] : void 0, d = n ? arguments[2] : arguments[1];
    return d._groupName = r, (this.callbacks[e] = this.callbacks[e] || []).push(d), this;
  }, t.once = function(e, s, o) {
    var n = this, r = arguments.length === 3, d = r ? arguments[1] : void 0, a = r ? arguments[2] : arguments[1];
    function l() {
      n.off(e, l), a.apply(this, arguments);
    }
    return this.on(e, d, l), this;
  }, t.releaseGroup = function(e) {
    this.callbacks = this.callbacks || {};
    var s, o, n, r;
    for (s in this.callbacks)
      for (r = this.callbacks[s], o = 0, n = r.length; o < n; o++)
        r[o]._groupName === e && (r.splice(o, 1), o--, n--);
    return this;
  }, t.off = function(e, s) {
    this.callbacks = this.callbacks || {};
    var o = this.callbacks[e], n;
    return o ? arguments.length === 1 ? (delete this.callbacks[e], this) : (n = o.indexOf(s), n !== -1 && (o.splice(n, 1), o.length === 0 && delete this.callbacks[e]), this) : this;
  }, t.emit = function(e) {
    this.callbacks = this.callbacks || {};
    var s = [].slice.call(arguments, 1), o = this.callbacks[e], n = this.getWildcardCallbacks(e), r, d, a;
    if (o)
      for (a = o.slice(), r = 0, d = a.length; r < d && a[r]; ++r)
        a[r].apply(this, s);
    if (n)
      for (d = n.length, a = n.slice(), r = 0, d = a.length; r < d && a[r]; ++r)
        a[r].apply(this, [e].concat(s));
    return this;
  }, t.getWildcardCallbacks = function(e) {
    this.callbacks = this.callbacks || {};
    var s, o, n = [];
    for (s in this.callbacks)
      o = s.split("*"), (s === "*" || o.length === 2 && e.slice(0, o[0].length) === o[0]) && (n = n.concat(this.callbacks[s]));
    return n;
  };
};
Rt.mixin(Rt);
var Wi = Qi;
function Yi(i, t) {
  var e = -1 / 0;
  i.getFloatFrequencyData(t);
  for (var s = 4, o = t.length; s < o; s++)
    t[s] > e && t[s] < 0 && (e = t[s]);
  return e;
}
var pe;
typeof window < "u" && (pe = window.AudioContext || window.webkitAudioContext);
var _ = null, Ji = function(i, s) {
  var e = new Wi();
  if (!pe)
    return e;
  var s = s || {}, o = s.smoothing || 0.1, n = s.interval || 50, r = s.threshold, d = s.play, a = s.history || 10, l = !0;
  _ = s.audioContext || _ || new pe();
  var p, h, c;
  c = _.createAnalyser(), c.fftSize = 512, c.smoothingTimeConstant = o, h = new Float32Array(c.frequencyBinCount), i.jquery && (i = i[0]), i instanceof HTMLAudioElement || i instanceof HTMLVideoElement ? (p = _.createMediaElementSource(i), typeof d > "u" && (d = !0), r = r || -50) : (p = _.createMediaStreamSource(i), r = r || -50), p.connect(c), d && c.connect(_.destination), e.speaking = !1, e.suspend = function() {
    return _.suspend();
  }, e.resume = function() {
    return _.resume();
  }, Object.defineProperty(e, "state", { get: function() {
    return _.state;
  } }), _.onstatechange = function() {
    e.emit("state_change", _.state);
  }, e.setThreshold = function(C) {
    r = C;
  }, e.setInterval = function(C) {
    n = C;
  }, e.stop = function() {
    l = !1, e.emit("volume_change", -100, r), e.speaking && (e.speaking = !1, e.emit("stopped_speaking")), c.disconnect(), p.disconnect();
  }, e.speakingHistory = [];
  for (var v = 0; v < a; v++)
    e.speakingHistory.push(0);
  var U = function() {
    setTimeout(function() {
      if (l) {
        var C = Yi(c, h);
        e.emit("volume_change", C, r);
        var _t = 0;
        if (C > r && !e.speaking) {
          for (var B = e.speakingHistory.length - 3; B < e.speakingHistory.length; B++)
            _t += e.speakingHistory[B];
          _t >= 2 && (e.speaking = !0, e.emit("speaking"));
        } else if (C < r && e.speaking) {
          for (var B = 0; B < e.speakingHistory.length; B++)
            _t += e.speakingHistory[B];
          _t == 0 && (e.speaking = !1, e.emit("stopped_speaking"));
        }
        e.speakingHistory.shift(), e.speakingHistory.push(0 + (C > r)), U();
      }
    }, n);
  };
  return U(), e;
};
const Ki = /* @__PURE__ */ Xi(Ji);
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const St = window, Ae = St.ShadowRoot && (St.ShadyCSS === void 0 || St.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, $e = Symbol(), je = /* @__PURE__ */ new WeakMap();
let gi = class {
  constructor(i, t, e) {
    if (this._$cssResult$ = !0, e !== $e)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = i, this.t = t;
  }
  get styleSheet() {
    let i = this.o;
    const t = this.t;
    if (Ae && i === void 0) {
      const e = t !== void 0 && t.length === 1;
      e && (i = je.get(t)), i === void 0 && ((this.o = i = new CSSStyleSheet()).replaceSync(this.cssText), e && je.set(t, i));
    }
    return i;
  }
  toString() {
    return this.cssText;
  }
};
const ts = (i) => new gi(typeof i == "string" ? i : i + "", void 0, $e), M = (i, ...t) => {
  const e = i.length === 1 ? i[0] : t.reduce((s, o, n) => s + ((r) => {
    if (r._$cssResult$ === !0)
      return r.cssText;
    if (typeof r == "number")
      return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(o) + i[n + 1], i[0]);
  return new gi(e, i, $e);
}, es = (i, t) => {
  Ae ? i.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet) : t.forEach((e) => {
    const s = document.createElement("style"), o = St.litNonce;
    o !== void 0 && s.setAttribute("nonce", o), s.textContent = e.cssText, i.appendChild(s);
  });
}, Be = Ae ? (i) => i : (i) => i instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const s of t.cssRules)
    e += s.cssText;
  return ts(e);
})(i) : i;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Wt;
const Ot = window, Ze = Ot.trustedTypes, is = Ze ? Ze.emptyScript : "", Ge = Ot.reactiveElementPolyfillSupport, ue = { toAttribute(i, t) {
  switch (t) {
    case Boolean:
      i = i ? is : null;
      break;
    case Object:
    case Array:
      i = i == null ? i : JSON.stringify(i);
  }
  return i;
}, fromAttribute(i, t) {
  let e = i;
  switch (t) {
    case Boolean:
      e = i !== null;
      break;
    case Number:
      e = i === null ? null : Number(i);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(i);
      } catch {
        e = null;
      }
  }
  return e;
} }, vi = (i, t) => t !== i && (t == t || i == i), Yt = { attribute: !0, type: String, converter: ue, reflect: !1, hasChanged: vi }, ge = "finalized";
let K = class extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this.u();
  }
  static addInitializer(i) {
    var t;
    this.finalize(), ((t = this.h) !== null && t !== void 0 ? t : this.h = []).push(i);
  }
  static get observedAttributes() {
    this.finalize();
    const i = [];
    return this.elementProperties.forEach((t, e) => {
      const s = this._$Ep(e, t);
      s !== void 0 && (this._$Ev.set(s, e), i.push(s));
    }), i;
  }
  static createProperty(i, t = Yt) {
    if (t.state && (t.attribute = !1), this.finalize(), this.elementProperties.set(i, t), !t.noAccessor && !this.prototype.hasOwnProperty(i)) {
      const e = typeof i == "symbol" ? Symbol() : "__" + i, s = this.getPropertyDescriptor(i, e, t);
      s !== void 0 && Object.defineProperty(this.prototype, i, s);
    }
  }
  static getPropertyDescriptor(i, t, e) {
    return { get() {
      return this[t];
    }, set(s) {
      const o = this[i];
      this[t] = s, this.requestUpdate(i, o, e);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(i) {
    return this.elementProperties.get(i) || Yt;
  }
  static finalize() {
    if (this.hasOwnProperty(ge))
      return !1;
    this[ge] = !0;
    const i = Object.getPrototypeOf(this);
    if (i.finalize(), i.h !== void 0 && (this.h = [...i.h]), this.elementProperties = new Map(i.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const t = this.properties, e = [...Object.getOwnPropertyNames(t), ...Object.getOwnPropertySymbols(t)];
      for (const s of e)
        this.createProperty(s, t[s]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(i) {
    const t = [];
    if (Array.isArray(i)) {
      const e = new Set(i.flat(1 / 0).reverse());
      for (const s of e)
        t.unshift(Be(s));
    } else
      i !== void 0 && t.push(Be(i));
    return t;
  }
  static _$Ep(i, t) {
    const e = t.attribute;
    return e === !1 ? void 0 : typeof e == "string" ? e : typeof i == "string" ? i.toLowerCase() : void 0;
  }
  u() {
    var i;
    this._$E_ = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (i = this.constructor.h) === null || i === void 0 || i.forEach((t) => t(this));
  }
  addController(i) {
    var t, e;
    ((t = this._$ES) !== null && t !== void 0 ? t : this._$ES = []).push(i), this.renderRoot !== void 0 && this.isConnected && ((e = i.hostConnected) === null || e === void 0 || e.call(i));
  }
  removeController(i) {
    var t;
    (t = this._$ES) === null || t === void 0 || t.splice(this._$ES.indexOf(i) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((i, t) => {
      this.hasOwnProperty(t) && (this._$Ei.set(t, this[t]), delete this[t]);
    });
  }
  createRenderRoot() {
    var i;
    const t = (i = this.shadowRoot) !== null && i !== void 0 ? i : this.attachShadow(this.constructor.shadowRootOptions);
    return es(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var i;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (i = this._$ES) === null || i === void 0 || i.forEach((t) => {
      var e;
      return (e = t.hostConnected) === null || e === void 0 ? void 0 : e.call(t);
    });
  }
  enableUpdating(i) {
  }
  disconnectedCallback() {
    var i;
    (i = this._$ES) === null || i === void 0 || i.forEach((t) => {
      var e;
      return (e = t.hostDisconnected) === null || e === void 0 ? void 0 : e.call(t);
    });
  }
  attributeChangedCallback(i, t, e) {
    this._$AK(i, e);
  }
  _$EO(i, t, e = Yt) {
    var s;
    const o = this.constructor._$Ep(i, e);
    if (o !== void 0 && e.reflect === !0) {
      const n = (((s = e.converter) === null || s === void 0 ? void 0 : s.toAttribute) !== void 0 ? e.converter : ue).toAttribute(t, e.type);
      this._$El = i, n == null ? this.removeAttribute(o) : this.setAttribute(o, n), this._$El = null;
    }
  }
  _$AK(i, t) {
    var e;
    const s = this.constructor, o = s._$Ev.get(i);
    if (o !== void 0 && this._$El !== o) {
      const n = s.getPropertyOptions(o), r = typeof n.converter == "function" ? { fromAttribute: n.converter } : ((e = n.converter) === null || e === void 0 ? void 0 : e.fromAttribute) !== void 0 ? n.converter : ue;
      this._$El = o, this[o] = r.fromAttribute(t, n.type), this._$El = null;
    }
  }
  requestUpdate(i, t, e) {
    let s = !0;
    i !== void 0 && (((e = e || this.constructor.getPropertyOptions(i)).hasChanged || vi)(this[i], t) ? (this._$AL.has(i) || this._$AL.set(i, t), e.reflect === !0 && this._$El !== i && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(i, e))) : s = !1), !this.isUpdatePending && s && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (t) {
      Promise.reject(t);
    }
    const i = this.scheduleUpdate();
    return i != null && await i, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var i;
    if (!this.isUpdatePending)
      return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((s, o) => this[o] = s), this._$Ei = void 0);
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), (i = this._$ES) === null || i === void 0 || i.forEach((s) => {
        var o;
        return (o = s.hostUpdate) === null || o === void 0 ? void 0 : o.call(s);
      }), this.update(e)) : this._$Ek();
    } catch (s) {
      throw t = !1, this._$Ek(), s;
    }
    t && this._$AE(e);
  }
  willUpdate(i) {
  }
  _$AE(i) {
    var t;
    (t = this._$ES) === null || t === void 0 || t.forEach((e) => {
      var s;
      return (s = e.hostUpdated) === null || s === void 0 ? void 0 : s.call(e);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(i)), this.updated(i);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(i) {
    return !0;
  }
  update(i) {
    this._$EC !== void 0 && (this._$EC.forEach((t, e) => this._$EO(e, this[e], t)), this._$EC = void 0), this._$Ek();
  }
  updated(i) {
  }
  firstUpdated(i) {
  }
};
K[ge] = !0, K.elementProperties = /* @__PURE__ */ new Map(), K.elementStyles = [], K.shadowRootOptions = { mode: "open" }, Ge == null || Ge({ ReactiveElement: K }), ((Wt = Ot.reactiveElementVersions) !== null && Wt !== void 0 ? Wt : Ot.reactiveElementVersions = []).push("1.6.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Jt;
const Ht = window, it = Ht.trustedTypes, ze = it ? it.createPolicy("lit-html", { createHTML: (i) => i }) : void 0, ve = "$lit$", I = `lit$${(Math.random() + "").slice(9)}$`, mi = "?" + I, ss = `<${mi}>`, Q = document, ft = () => Q.createComment(""), bt = (i) => i === null || typeof i != "object" && typeof i != "function", fi = Array.isArray, os = (i) => fi(i) || typeof (i == null ? void 0 : i[Symbol.iterator]) == "function", Kt = `[ 	
\f\r]`, ht = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, qe = /-->/g, Fe = />/g, G = RegExp(`>|${Kt}(?:([^\\s"'>=/]+)(${Kt}*=${Kt}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), Xe = /'/g, Qe = /"/g, bi = /^(?:script|style|textarea|title)$/i, ns = (i) => (t, ...e) => ({ _$litType$: i, strings: t, values: e }), u = ns(1), W = Symbol.for("lit-noChange"), b = Symbol.for("lit-nothing"), We = /* @__PURE__ */ new WeakMap(), q = Q.createTreeWalker(Q, 129, null, !1), rs = (i, t) => {
  const e = i.length - 1, s = [];
  let o, n = t === 2 ? "<svg>" : "", r = ht;
  for (let a = 0; a < e; a++) {
    const l = i[a];
    let p, h, c = -1, v = 0;
    for (; v < l.length && (r.lastIndex = v, h = r.exec(l), h !== null); )
      v = r.lastIndex, r === ht ? h[1] === "!--" ? r = qe : h[1] !== void 0 ? r = Fe : h[2] !== void 0 ? (bi.test(h[2]) && (o = RegExp("</" + h[2], "g")), r = G) : h[3] !== void 0 && (r = G) : r === G ? h[0] === ">" ? (r = o ?? ht, c = -1) : h[1] === void 0 ? c = -2 : (c = r.lastIndex - h[2].length, p = h[1], r = h[3] === void 0 ? G : h[3] === '"' ? Qe : Xe) : r === Qe || r === Xe ? r = G : r === qe || r === Fe ? r = ht : (r = G, o = void 0);
    const U = r === G && i[a + 1].startsWith("/>") ? " " : "";
    n += r === ht ? l + ss : c >= 0 ? (s.push(p), l.slice(0, c) + ve + l.slice(c) + I + U) : l + I + (c === -2 ? (s.push(void 0), a) : U);
  }
  const d = n + (i[e] || "<?>") + (t === 2 ? "</svg>" : "");
  if (!Array.isArray(i) || !i.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return [ze !== void 0 ? ze.createHTML(d) : d, s];
};
class wt {
  constructor({ strings: t, _$litType$: e }, s) {
    let o;
    this.parts = [];
    let n = 0, r = 0;
    const d = t.length - 1, a = this.parts, [l, p] = rs(t, e);
    if (this.el = wt.createElement(l, s), q.currentNode = this.el.content, e === 2) {
      const h = this.el.content, c = h.firstChild;
      c.remove(), h.append(...c.childNodes);
    }
    for (; (o = q.nextNode()) !== null && a.length < d; ) {
      if (o.nodeType === 1) {
        if (o.hasAttributes()) {
          const h = [];
          for (const c of o.getAttributeNames())
            if (c.endsWith(ve) || c.startsWith(I)) {
              const v = p[r++];
              if (h.push(c), v !== void 0) {
                const U = o.getAttribute(v.toLowerCase() + ve).split(I), C = /([.?@])?(.*)/.exec(v);
                a.push({ type: 1, index: n, name: C[2], strings: U, ctor: C[1] === "." ? ls : C[1] === "?" ? hs : C[1] === "@" ? cs : Pt });
              } else
                a.push({ type: 6, index: n });
            }
          for (const c of h)
            o.removeAttribute(c);
        }
        if (bi.test(o.tagName)) {
          const h = o.textContent.split(I), c = h.length - 1;
          if (c > 0) {
            o.textContent = it ? it.emptyScript : "";
            for (let v = 0; v < c; v++)
              o.append(h[v], ft()), q.nextNode(), a.push({ type: 2, index: ++n });
            o.append(h[c], ft());
          }
        }
      } else if (o.nodeType === 8)
        if (o.data === mi)
          a.push({ type: 2, index: n });
        else {
          let h = -1;
          for (; (h = o.data.indexOf(I, h + 1)) !== -1; )
            a.push({ type: 7, index: n }), h += I.length - 1;
        }
      n++;
    }
  }
  static createElement(t, e) {
    const s = Q.createElement("template");
    return s.innerHTML = t, s;
  }
}
function st(i, t, e = i, s) {
  var o, n, r, d;
  if (t === W)
    return t;
  let a = s !== void 0 ? (o = e._$Co) === null || o === void 0 ? void 0 : o[s] : e._$Cl;
  const l = bt(t) ? void 0 : t._$litDirective$;
  return (a == null ? void 0 : a.constructor) !== l && ((n = a == null ? void 0 : a._$AO) === null || n === void 0 || n.call(a, !1), l === void 0 ? a = void 0 : (a = new l(i), a._$AT(i, e, s)), s !== void 0 ? ((r = (d = e)._$Co) !== null && r !== void 0 ? r : d._$Co = [])[s] = a : e._$Cl = a), a !== void 0 && (t = st(i, a._$AS(i, t.values), a, s)), t;
}
class as {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    var e;
    const { el: { content: s }, parts: o } = this._$AD, n = ((e = t == null ? void 0 : t.creationScope) !== null && e !== void 0 ? e : Q).importNode(s, !0);
    q.currentNode = n;
    let r = q.nextNode(), d = 0, a = 0, l = o[0];
    for (; l !== void 0; ) {
      if (d === l.index) {
        let p;
        l.type === 2 ? p = new $t(r, r.nextSibling, this, t) : l.type === 1 ? p = new l.ctor(r, l.name, l.strings, this, t) : l.type === 6 && (p = new ps(r, this, t)), this._$AV.push(p), l = o[++a];
      }
      d !== (l == null ? void 0 : l.index) && (r = q.nextNode(), d++);
    }
    return q.currentNode = Q, n;
  }
  v(t) {
    let e = 0;
    for (const s of this._$AV)
      s !== void 0 && (s.strings !== void 0 ? (s._$AI(t, s, e), e += s.strings.length - 2) : s._$AI(t[e])), e++;
  }
}
class $t {
  constructor(t, e, s, o) {
    var n;
    this.type = 2, this._$AH = b, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = s, this.options = o, this._$Cp = (n = o == null ? void 0 : o.isConnected) === null || n === void 0 || n;
  }
  get _$AU() {
    var t, e;
    return (e = (t = this._$AM) === null || t === void 0 ? void 0 : t._$AU) !== null && e !== void 0 ? e : this._$Cp;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = st(this, t, e), bt(t) ? t === b || t == null || t === "" ? (this._$AH !== b && this._$AR(), this._$AH = b) : t !== this._$AH && t !== W && this._(t) : t._$litType$ !== void 0 ? this.g(t) : t.nodeType !== void 0 ? this.$(t) : os(t) ? this.T(t) : this._(t);
  }
  k(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  $(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.k(t));
  }
  _(t) {
    this._$AH !== b && bt(this._$AH) ? this._$AA.nextSibling.data = t : this.$(Q.createTextNode(t)), this._$AH = t;
  }
  g(t) {
    var e;
    const { values: s, _$litType$: o } = t, n = typeof o == "number" ? this._$AC(t) : (o.el === void 0 && (o.el = wt.createElement(o.h, this.options)), o);
    if (((e = this._$AH) === null || e === void 0 ? void 0 : e._$AD) === n)
      this._$AH.v(s);
    else {
      const r = new as(n, this), d = r.u(this.options);
      r.v(s), this.$(d), this._$AH = r;
    }
  }
  _$AC(t) {
    let e = We.get(t.strings);
    return e === void 0 && We.set(t.strings, e = new wt(t)), e;
  }
  T(t) {
    fi(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let s, o = 0;
    for (const n of t)
      o === e.length ? e.push(s = new $t(this.k(ft()), this.k(ft()), this, this.options)) : s = e[o], s._$AI(n), o++;
    o < e.length && (this._$AR(s && s._$AB.nextSibling, o), e.length = o);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var s;
    for ((s = this._$AP) === null || s === void 0 || s.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const o = t.nextSibling;
      t.remove(), t = o;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this._$Cp = t, (e = this._$AP) === null || e === void 0 || e.call(this, t));
  }
}
class Pt {
  constructor(t, e, s, o, n) {
    this.type = 1, this._$AH = b, this._$AN = void 0, this.element = t, this.name = e, this._$AM = o, this.options = n, s.length > 2 || s[0] !== "" || s[1] !== "" ? (this._$AH = Array(s.length - 1).fill(new String()), this.strings = s) : this._$AH = b;
  }
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t, e = this, s, o) {
    const n = this.strings;
    let r = !1;
    if (n === void 0)
      t = st(this, t, e, 0), r = !bt(t) || t !== this._$AH && t !== W, r && (this._$AH = t);
    else {
      const d = t;
      let a, l;
      for (t = n[0], a = 0; a < n.length - 1; a++)
        l = st(this, d[s + a], e, a), l === W && (l = this._$AH[a]), r || (r = !bt(l) || l !== this._$AH[a]), l === b ? t = b : t !== b && (t += (l ?? "") + n[a + 1]), this._$AH[a] = l;
    }
    r && !o && this.j(t);
  }
  j(t) {
    t === b ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class ls extends Pt {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === b ? void 0 : t;
  }
}
const ds = it ? it.emptyScript : "";
class hs extends Pt {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    t && t !== b ? this.element.setAttribute(this.name, ds) : this.element.removeAttribute(this.name);
  }
}
class cs extends Pt {
  constructor(t, e, s, o, n) {
    super(t, e, s, o, n), this.type = 5;
  }
  _$AI(t, e = this) {
    var s;
    if ((t = (s = st(this, t, e, 0)) !== null && s !== void 0 ? s : b) === W)
      return;
    const o = this._$AH, n = t === b && o !== b || t.capture !== o.capture || t.once !== o.once || t.passive !== o.passive, r = t !== b && (o === b || n);
    n && this.element.removeEventListener(this.name, this, o), r && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e, s;
    typeof this._$AH == "function" ? this._$AH.call((s = (e = this.options) === null || e === void 0 ? void 0 : e.host) !== null && s !== void 0 ? s : this.element, t) : this._$AH.handleEvent(t);
  }
}
class ps {
  constructor(t, e, s) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = s;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    st(this, t);
  }
}
const Ye = Ht.litHtmlPolyfillSupport;
Ye == null || Ye(wt, $t), ((Jt = Ht.litHtmlVersions) !== null && Jt !== void 0 ? Jt : Ht.litHtmlVersions = []).push("2.7.4");
const us = (i, t, e) => {
  var s, o;
  const n = (s = e == null ? void 0 : e.renderBefore) !== null && s !== void 0 ? s : t;
  let r = n._$litPart$;
  if (r === void 0) {
    const d = (o = e == null ? void 0 : e.renderBefore) !== null && o !== void 0 ? o : null;
    n._$litPart$ = r = new $t(t.insertBefore(ft(), d), d, void 0, e ?? {});
  }
  return r._$AI(i), r;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var te, ee;
let A = class extends K {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var i, t;
    const e = super.createRenderRoot();
    return (i = (t = this.renderOptions).renderBefore) !== null && i !== void 0 || (t.renderBefore = e.firstChild), e;
  }
  update(i) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(i), this._$Do = us(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var i;
    super.connectedCallback(), (i = this._$Do) === null || i === void 0 || i.setConnected(!0);
  }
  disconnectedCallback() {
    var i;
    super.disconnectedCallback(), (i = this._$Do) === null || i === void 0 || i.setConnected(!1);
  }
  render() {
    return W;
  }
};
A.finalized = !0, A._$litElement$ = !0, (te = globalThis.litElementHydrateSupport) === null || te === void 0 || te.call(globalThis, { LitElement: A });
const Je = globalThis.litElementPolyfillSupport;
Je == null || Je({ LitElement: A });
((ee = globalThis.litElementVersions) !== null && ee !== void 0 ? ee : globalThis.litElementVersions = []).push("3.3.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const V = (i) => (t) => typeof t == "function" ? ((e, s) => (customElements.define(e, s), s))(i, t) : ((e, s) => {
  const { kind: o, elements: n } = s;
  return { kind: o, elements: n, finisher(r) {
    customElements.define(e, r);
  } };
})(i, t);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const gs = (i, t) => t.kind === "method" && t.descriptor && !("value" in t.descriptor) ? { ...t, finisher(e) {
  e.createProperty(t.key, i);
} } : { kind: "field", key: Symbol(), placement: "own", descriptor: {}, originalKey: t.key, initializer() {
  typeof t.initializer == "function" && (this[t.key] = t.initializer.call(this));
}, finisher(e) {
  e.createProperty(t.key, i);
} }, vs = (i, t, e) => {
  t.constructor.createProperty(e, i);
};
function m(i) {
  return (t, e) => e !== void 0 ? vs(i, t, e) : gs(i, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function H(i) {
  return m({ ...i, state: !0 });
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var ie;
((ie = window.HTMLSlotElement) === null || ie === void 0 ? void 0 : ie.prototype.assignedElements) != null;
function E() {
  return Math.random().toString(16).slice(2);
}
function ms(i) {
  if (i == null || isNaN(i) || !isFinite(i))
    return "";
  const t = Math.floor(i / 3600), e = Math.floor(i % 3600 / 60), s = Math.round(i % 60);
  return [
    t,
    e > 9 ? e : t ? "0" + e : e || "0",
    s > 9 ? s : "0" + s
  ].filter(Boolean).join(":");
}
class fs {
  constructor(t, e) {
    this.url = t, this.token = e;
  }
  sendText(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    return e == null && (e = E()), s == null && (s = E()), fetch(this.url, {
      method: "POST",
      body: JSON.stringify({
        chat_id: e,
        messageID: s,
        text: t,
        metadata: null
      }),
      headers: {
        "Content-Type": "application/json",
        token: this.token
      }
    }).then((o) => o.json());
  }
  // @ts-ignore
  sendImage(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    throw new Error("Sending image to conversation manager is not currently supported");
  }
  // @ts-ignore
  sendLocation(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    throw new Error("Sending location to conversation manager is not currently supported");
  }
  // @ts-ignore
  sendAudio(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    throw new Error("Sending audio to conversation manager is not currently supported");
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const bs = (i) => i.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const wi = { ATTRIBUTE: 1, CHILD: 2, PROPERTY: 3, BOOLEAN_ATTRIBUTE: 4, EVENT: 5, ELEMENT: 6 }, Ci = (i) => (...t) => ({ _$litDirective$: i, values: t });
class yi {
  constructor(t) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(t, e, s) {
    this._$Ct = t, this._$AM = e, this._$Ci = s;
  }
  _$AS(t, e) {
    return this.update(t, e);
  }
  update(t, e) {
    return this.render(...e);
  }
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ut = (i, t) => {
  var e, s;
  const o = i._$AN;
  if (o === void 0)
    return !1;
  for (const n of o)
    (s = (e = n)._$AO) === null || s === void 0 || s.call(e, t, !1), ut(n, t);
  return !0;
}, Ut = (i) => {
  let t, e;
  do {
    if ((t = i._$AM) === void 0)
      break;
    e = t._$AN, e.delete(i), i = t;
  } while ((e == null ? void 0 : e.size) === 0);
}, xi = (i) => {
  for (let t; t = i._$AM; i = t) {
    let e = t._$AN;
    if (e === void 0)
      t._$AN = e = /* @__PURE__ */ new Set();
    else if (e.has(i))
      break;
    e.add(i), ys(t);
  }
};
function ws(i) {
  this._$AN !== void 0 ? (Ut(this), this._$AM = i, xi(this)) : this._$AM = i;
}
function Cs(i, t = !1, e = 0) {
  const s = this._$AH, o = this._$AN;
  if (o !== void 0 && o.size !== 0)
    if (t)
      if (Array.isArray(s))
        for (let n = e; n < s.length; n++)
          ut(s[n], !1), Ut(s[n]);
      else
        s != null && (ut(s, !1), Ut(s));
    else
      ut(this, i);
}
const ys = (i) => {
  var t, e, s, o;
  i.type == wi.CHILD && ((t = (s = i)._$AP) !== null && t !== void 0 || (s._$AP = Cs), (e = (o = i)._$AQ) !== null && e !== void 0 || (o._$AQ = ws));
};
class xs extends yi {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(t, e, s) {
    super._$AT(t, e, s), xi(this), this.isConnected = t._$AU;
  }
  _$AO(t, e = !0) {
    var s, o;
    t !== this.isConnected && (this.isConnected = t, t ? (s = this.reconnected) === null || s === void 0 || s.call(this) : (o = this.disconnected) === null || o === void 0 || o.call(this)), e && (ut(this, t), Ut(this));
  }
  setValue(t) {
    if (bs(this._$Ct))
      this._$Ct._$AI(t, this);
    else {
      const e = [...this._$Ct._$AH];
      e[this._$Ci] = t, this._$Ct._$AI(e, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Dt = () => new As();
let As = class {
};
const se = /* @__PURE__ */ new WeakMap(), Tt = Ci(class extends xs {
  render(i) {
    return b;
  }
  update(i, [t]) {
    var e;
    const s = t !== this.G;
    return s && this.G !== void 0 && this.ot(void 0), (s || this.rt !== this.lt) && (this.G = t, this.ct = (e = i.options) === null || e === void 0 ? void 0 : e.host, this.ot(this.lt = i.element)), b;
  }
  ot(i) {
    var t;
    if (typeof this.G == "function") {
      const e = (t = this.ct) !== null && t !== void 0 ? t : globalThis;
      let s = se.get(e);
      s === void 0 && (s = /* @__PURE__ */ new WeakMap(), se.set(e, s)), s.get(this.G) !== void 0 && this.G.call(this.ct, void 0), s.set(this.G, i), i !== void 0 && this.G.call(this.ct, i);
    } else
      this.G.value = i;
  }
  get rt() {
    var i, t, e;
    return typeof this.G == "function" ? (t = se.get((i = this.ct) !== null && i !== void 0 ? i : globalThis)) === null || t === void 0 ? void 0 : t.get(this.G) : (e = this.G) === null || e === void 0 ? void 0 : e.value;
  }
  disconnected() {
    this.rt === this.lt && this.ot(void 0);
  }
  reconnected() {
    this.ot(this.lt);
  }
});
var $s = Object.defineProperty, ks = Object.getOwnPropertyDescriptor, _s = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? ks(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && $s(t, e, o), o;
};
let me = class extends A {
  render() {
    return u`
            <div class="sound-animation">
                <div class="sound-icon">
                    <div class="sound-wave">
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                        <i class="bar"></i>
                    </div>
                </div>
            </div>
        `;
  }
};
me.styles = M`
      :host {
        width: 100%;
      }
        .sound-icon {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .sound-wave {
          width: 100%;
          height: 43px;
          display: flex;
          justify-content: space-around;
          align-items: center;
          transform: scaleY(0.6);
        }
        
        .bar {
          display: block;
          width: 2px;
          margin-right: 1px;
          height: 90px;
          background: rgba(30, 30, 30, 0.75);
          animation: sound 0ms -800ms linear infinite alternate;
          transition: height 0.8s;
        }
        
        @-webkit-keyframes sound {
          0% {
            opacity: 0.35;
            height: 6px;
          }
          100% {
            opacity: 1;
            height: 46px;
          }
        }
        
        @keyframes sound {
          0% {
            opacity: 0.35;
            height: 6px;
          }
          100% {
            opacity: 1;
            height: 46px;
          }
        }
        .bar:nth-child(1) {
          height: 2px;
          animation-duration: 474ms;
        }
        
        .bar:nth-child(2) {
          height: 10px;
          animation-duration: 433ms;
        }
        
        .bar:nth-child(3) {
          height: 18px;
          animation-duration: 407ms;
        }
        
        .bar:nth-child(4) {
          height: 26px;
          animation-duration: 458ms;
        }
        
        .bar:nth-child(5) {
          height: 30px;
          animation-duration: 400ms;
        }
        
        .bar:nth-child(6) {
          height: 32px;
          animation-duration: 427ms;
        }
        
        .bar:nth-child(7) {
          height: 34px;
          animation-duration: 441ms;
        }
        
        .bar:nth-child(8) {
          height: 36px;
          animation-duration: 419ms;
        }
        
        .bar:nth-child(9) {
          height: 40px;
          animation-duration: 487ms;
        }
        
        .bar:nth-child(10) {
          height: 46px;
          animation-duration: 442ms;
        }
        
        .bar:nth-child(11) {
          height: 2px;
          animation-duration: 474ms;
        }
        
        .bar:nth-child(12) {
          height: 10px;
          animation-duration: 433ms;
        }
        
        .bar:nth-child(13) {
          height: 18px;
          animation-duration: 407ms;
        }
        
        .bar:nth-child(14) {
          height: 26px;
          animation-duration: 458ms;
        }
        
        .bar:nth-child(15) {
          height: 30px;
          animation-duration: 400ms;
        }
        
        .bar:nth-child(16) {
          height: 32px;
          animation-duration: 427ms;
        }
        
        .bar:nth-child(17) {
          height: 34px;
          animation-duration: 441ms;
        }
        
        .bar:nth-child(18) {
          height: 36px;
          animation-duration: 419ms;
        }
        
        .bar:nth-child(19) {
          height: 40px;
          animation-duration: 487ms;
        }
        
        .bar:nth-child(20) {
          height: 46px;
          animation-duration: 442ms;
        }
    `;
me = _s([
  V("sound-wave")
], me);
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ct = Ci(class extends yi {
  constructor(i) {
    var t;
    if (super(i), i.type !== wi.ATTRIBUTE || i.name !== "class" || ((t = i.strings) === null || t === void 0 ? void 0 : t.length) > 2)
      throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(i) {
    return " " + Object.keys(i).filter((t) => i[t]).join(" ") + " ";
  }
  update(i, [t]) {
    var e, s;
    if (this.it === void 0) {
      this.it = /* @__PURE__ */ new Set(), i.strings !== void 0 && (this.nt = new Set(i.strings.join(" ").split(/\s/).filter((n) => n !== "")));
      for (const n in t)
        t[n] && !(!((e = this.nt) === null || e === void 0) && e.has(n)) && this.it.add(n);
      return this.render(t);
    }
    const o = i.element.classList;
    this.it.forEach((n) => {
      n in t || (o.remove(n), this.it.delete(n));
    });
    for (const n in t) {
      const r = !!t[n];
      r === this.it.has(n) || !((s = this.nt) === null || s === void 0) && s.has(n) || (r ? (o.add(n), this.it.add(n)) : (o.remove(n), this.it.delete(n)));
    }
    return W;
  }
});
var Es = Object.defineProperty, Ss = Object.getOwnPropertyDescriptor, jt = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Ss(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Es(t, e, o), o;
};
let ot = class extends A {
  constructor() {
    super(...arguments), this.locale = "en", this.attachmentsOpen = !1, this.isRecordingAudio = !1, this.mediaRecorder = null, this.inputRef = Dt(), this.photosRef = Dt();
  }
  onAttach() {
    this.attachmentsOpen = !this.attachmentsOpen;
  }
  onPhotosAttach() {
    var i;
    (i = this.photosRef.value) == null || i.click();
  }
  onLocationAttach() {
    const i = {
      enableHighAccuracy: !0,
      timeout: 5e3,
      maximumAge: 0
    };
    navigator.geolocation.getCurrentPosition((t) => {
      const e = t.coords;
      this.dispatchEvent(new CustomEvent("user-location", {
        detail: {
          location: {
            latitude: e.latitude,
            longitude: e.longitude
          },
          datetime: /* @__PURE__ */ new Date()
        }
      }));
    }, (t) => {
      console.warn(`ERROR(${t.code}): ${t.message}`);
    }, i);
  }
  onUploadPhoto(i) {
    this.dispatchEvent(new CustomEvent("user-image", {
      detail: {
        image: i.target.files[0],
        datetime: /* @__PURE__ */ new Date()
      }
    })), i.target.value = "";
  }
  startRecordAudio() {
    this.mediaRecorder == null ? navigator.mediaDevices.getUserMedia({ audio: !0 }).then((i) => {
      this.mediaRecorder = new MediaRecorder(i), this.mediaRecorder.addEventListener("dataavailable", (t) => {
        var e;
        this.dispatchEvent(new CustomEvent("user-audio", {
          detail: {
            audio: new Blob([t.data], {
              type: (e = this.mediaRecorder) == null ? void 0 : e.mimeType
            }),
            datetime: /* @__PURE__ */ new Date()
          }
        }));
      }), this.mediaRecorder.addEventListener("start", () => {
        this.isRecordingAudio = !0;
      }), this.mediaRecorder.addEventListener("stop", () => {
        this.isRecordingAudio = !1;
      }), this.mediaRecorder.start();
    }) : this.mediaRecorder.start();
  }
  stopRecordAudio() {
    var i, t;
    (i = this.mediaRecorder) == null || i.stop(), (t = this.mediaRecorder) == null || t.stream.getTracks().forEach((e) => e.stop());
  }
  onSubmit(i) {
    i.preventDefault();
    const t = new FormData(i.target).get("message");
    this.dispatchEvent(new CustomEvent("user-text", {
      detail: {
        text: t,
        datetime: /* @__PURE__ */ new Date()
      }
    })), i.target.reset();
  }
  render() {
    let i;
    return this.isRecordingAudio ? i = u`
                <sound-wave></sound-wave>
                <div id="stop-audio-btn-wrapper">
                    <button id="stop-audio-btn" type="button" @click=${this.stopRecordAudio.bind(this)}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path style="fill:white"
                                  d="M392,432H120a40,40,0,0,1-40-40V120a40,40,0,0,1,40-40H392a40,40,0,0,1,40,40V392A40,40,0,0,1,392,432Z"/>
                        </svg>
                    </button>
                </div>
            ` : i = u`
                <div id="content-wrapper">
                    <button type="button" id="attach-btn" class=${Ct({ open: this.attachmentsOpen })}
                            @click=${this.onAttach.bind(this)}>
                        <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                            <g id="SVGRepo_iconCarrier">
                                <path fill="#3b3b3b" fill-rule="evenodd"
                                      d="M9 17a1 1 0 102 0v-6h6a1 1 0 100-2h-6V3a1 1 0 10-2 0v6H3a1 1 0 000 2h6v6z"></path>
                            </g>
                        </svg>
                    </button>
                    <div class="input-wrapper">
                        <input
                                class="input"
                                type="text"
                                name="message"
                                required
                                autocomplete="off"
                                placeholder=${{
      en: "Type your message…",
      es: "Escribe tu mensaje..."
    }[this.locale]}
                                ${Tt(this.inputRef)}
                        />
                    </div>
                </div>
                <div id="attachments" class=${Ct({ hidden: !this.attachmentsOpen })}>
                    <div class="attachment">
                        <button class="attachment-btn" type="button" @click=${this.onPhotosAttach.bind(this)}>
                            <svg fill="#77BA99" viewBox="0 0 36 36" preserveAspectRatio="xMidYMid meet"
                                 xmlns="http://www.w3.org/2000/svg" stroke="#77BA99">
                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                <g id="SVGRepo_iconCarrier">
                                    <path d="M30.14,3h0a1,1,0,0,0-1-1h-22a1,1,0,0,0-1,1h0V4h24Z"
                                          class="clr-i-solid clr-i-solid-path-1"></path>
                                    <path d="M32.12,7V7a1,1,0,0,0-1-1h-26a1,1,0,0,0-1,1h0V8h28Z"
                                          class="clr-i-solid clr-i-solid-path-2"></path>
                                    <path d="M32.12,10H3.88A1.88,1.88,0,0,0,2,11.88V30.12A1.88,1.88,0,0,0,3.88,32H32.12A1.88,1.88,0,0,0,34,30.12V11.88A1.88,1.88,0,0,0,32.12,10ZM8.56,13.45a3,3,0,1,1-3,3A3,3,0,0,1,8.56,13.45ZM30,28h-24l7.46-7.47a.71.71,0,0,1,1,0l3.68,3.68L23.21,19a.71.71,0,0,1,1,0L30,24.79Z"
                                          class="clr-i-solid clr-i-solid-path-3"></path>
                                </g>
                            </svg>
                        </button>
                        <input type="file" name="photos" ${Tt(this.photosRef)} id="photos-file"
                               accept="image/png, image/jpeg" @change=${this.onUploadPhoto.bind(this)}>
                        <p class="attachment-text">Photos</p>
                    </div>
                    <div class="attachment">
                        <button class="attachment-btn" type="button" @click=${this.onLocationAttach.bind(this)}>
                            <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="#000000">
                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                <g id="SVGRepo_iconCarrier">
                                    <path fill="#D33F49"
                                          d="M32,0C18.746,0,8,10.746,8,24c0,5.219,1.711,10.008,4.555,13.93c0.051,0.094,0.059,0.199,0.117,0.289l16,24 C29.414,63.332,30.664,64,32,64s2.586-0.668,3.328-1.781l16-24c0.059-0.09,0.066-0.195,0.117-0.289C54.289,34.008,56,29.219,56,24 C56,10.746,45.254,0,32,0z M32,32c-4.418,0-8-3.582-8-8s3.582-8,8-8s8,3.582,8,8S36.418,32,32,32z"></path>
                                </g>
                            </svg>
                        </button>
                        <p class="attachment-text">Location</p>
                    </div>
                    <div class="attachment">
                        <button class="attachment-btn" type="button" @click=${this.startRecordAudio.bind(this)}>
                            <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="#000000">
                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                <g id="SVGRepo_iconCarrier">
                                    <g>
                                        <path fill="#7D82B8"
                                              d="M32,44c6.629,0,12-5.371,12-12V12c0-6.629-5.371-12-12-12S20,5.371,20,12v20C20,38.629,25.371,44,32,44z"></path>
                                        <path fill="#7D82B8"
                                              d="M52,28c-2.211,0-4,1.789-4,4c0,8.836-7.164,16-16,16s-16-7.164-16-16c0-2.211-1.789-4-4-4s-4,1.789-4,4 c0,11.887,8.656,21.73,20,23.641V60c0,2.211,1.789,4,4,4s4-1.789,4-4v-4.359C47.344,53.73,56,43.887,56,32 C56,29.789,54.211,28,52,28z"></path>
                                    </g>
                                </g>
                            </svg>
                        </button>
                        <p class="attachment-text">Audio</p>
                    </div>
                </div>
            `, u`
            <form @submit=${this.onSubmit.bind(this)}>
                <div class="wrapper">
                    ${i}
                </div>
            </form>
        `;
  }
};
ot.styles = M`
      :host {
        width: 100%;
        box-sizing: border-box;
        position: relative;
      }

      form {
        margin: 0;
      }

      .wrapper {
        padding: 1rem;
        height: 100%;
        display: flex;
        align-items: center;
        flex-direction: column;
        background-color: rgb(226, 232, 240);
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        gap: 14px;
      }

      .input-wrapper {
        background-color: rgb(59, 59, 59);
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        padding: 10px;
        border-radius: 4px;
      }

      .input {
        all: unset;
        color: rgb(255, 255, 255, 1);
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        font-size: 14px;
      }

      .input::placeholder {
        color: rgba(255, 255, 255, 0.8)
      }

      #attach-btn {
        all: unset;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        width: 30px;
        height: 30px;
        border: 2px solid rgb(59, 59, 59);

        & svg {
          transition-duration: 0.3s;
          transition-property: transform;
        }

        & svg {
          width: 70%;
          height: 70%;
        }

        &:hover {
          background-color: rgb(59, 59, 59);
        }

        &:hover svg path {
          fill: white;
        }
      }

      #content-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 100%;
      }

      #attachments {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        grid-column-gap: 30px;
        grid-row-gap: 20px;
        width: 100%;
      }

      .attachment {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
      }

      .attachment-text {
        all: unset;
        font-size: 12px;
        font-weight: 500;
      }

      .attachment-btn {
        all: unset;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        aspect-ratio: 1/1;
        border-radius: 50%;
        background-color: rgb(59, 59, 59);

        & svg {
          width: 55%;
          height: 55%;
        }
      }

      .hidden {
        display: none !important;
      }

      #attach-btn.open svg {
        transform: rotate(45deg);
      }

      #photos-file {
        display: none;
      }
      
      #stop-audio-btn-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        background-color: rgba(30, 30, 30);
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        cursor: pointer;
        padding: 10px;
      }
      
      #stop-audio-btn {
        all: unset;
        width: 22px;
        height: 22px;
        
        & svg {
          width: 100%;
          height: 100%;
        }
      }
    `;
jt([
  m()
], ot.prototype, "locale", 2);
jt([
  H()
], ot.prototype, "attachmentsOpen", 2);
jt([
  H()
], ot.prototype, "isRecordingAudio", 2);
ot = jt([
  V("maia-chat-input")
], ot);
var Ls = Object.defineProperty, Ms = Object.getOwnPropertyDescriptor, Bt = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Ms(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Ls(t, e, o), o;
};
let nt = class extends A {
  constructor() {
    super(...arguments), this.isPlayingAudio = !1, this.audioRef = Dt();
  }
  toggleAudio() {
    var i, t;
    this.isPlayingAudio ? ((i = this.audioRef.value) == null || i.pause(), this.isPlayingAudio = !this.isPlayingAudio) : (t = this.audioRef.value) == null || t.play().then(() => {
      this.isPlayingAudio = !this.isPlayingAudio;
    }).catch(console.error);
  }
  onDurationChange(i) {
    this.audioDuration = i.target.duration;
  }
  onEndAudio() {
    this.isPlayingAudio = !1;
  }
  onMetadata(i) {
    this.audioDuration = i.target.duration;
  }
  render() {
    return u`          
            <audio src=${this.audio} ${Tt(this.audioRef)} preload="metadata" @loadedmetadata=${this.onMetadata.bind(this)} @durationchange=${this.onDurationChange.bind(this)} @ended=${this.onEndAudio.bind(this)}></audio>
            <div class="audio-wrapper">
                <div class="audio-icon">
                    <svg width="118" height="21" viewBox="0 0 118 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="7" y1="6" x2="7" y2="15" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="1" y1="10" x2="1" y2="11" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="42" y1="10" x2="42" y2="11" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="65" y1="10" x2="65" y2="11" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="82" y1="10" x2="82" y2="11" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="105" y1="10" x2="105" y2="11" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="117" y1="10" x2="117" y2="11" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="13" y1="1" x2="13" y2="20" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="19" y1="6" x2="19" y2="15" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="24" y1="3" x2="24" y2="18" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="30" y1="1" x2="30" y2="18" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="53" y1="1" x2="53" y2="18" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="36" y1="6" x2="36" y2="15" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="59" y1="6" x2="59" y2="15" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="76" y1="7" x2="76" y2="13" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="88" y1="7" x2="88" y2="13" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="99" y1="7" x2="99" y2="13" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="111" y1="7" x2="111" y2="13" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="70" y1="3" x2="70" y2="18" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="94" y1="1" x2="94" y2="18" stroke="black" stroke-width="2" stroke-linecap="round"/>
                        <line x1="48" y1="6" x2="48" y2="15" stroke="black" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </div>
                <div class="audio-actions">
                    <p class="audio-duration">${ms(this.audioDuration)}</p>
                    <button class="audio-play" @click=${this.toggleAudio.bind(this)}>
                        ${this.isPlayingAudio ? u`
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="144 80 224 352">   
                                    <path fill="white" d="M208,432H160a16,16,0,0,1-16-16V96a16,16,0,0,1,16-16h48a16,16,0,0,1,16,16V416A16,16,0,0,1,208,432Z"></path>                                                 
                                    <path fill="white" d="M352,432H304a16,16,0,0,1-16-16V96a16,16,0,0,1,16-16h48a16,16,0,0,1,16,16V416A16,16,0,0,1,352,432Z"></path>     
                                </svg>
                                ` : u`
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                    <path fill="white" d="M133,440a35.37,35.37,0,0,1-17.5-4.67c-12-6.8-19.46-20-19.46-34.33V111c0-14.37,7.46-27.53,19.46-34.33a35.13,35.13,0,0,1,35.77.45L399.12,225.48a36,36,0,0,1,0,61L151.23,434.88A35.5,35.5,0,0,1,133,440Z"/>
                                </svg>
                                `}
                    </button>
                </div>
            </div>
        `;
  }
};
nt.styles = M`
      .audio-wrapper {
        background-color: white;
        display: flex;
        align-items: center;
        padding: 6px;
        border-radius: 4px;
      }
      
      .audio-icon {
       padding: 0 16px; 
        display: flex;
        align-items: center;
      }
      
      .audio-duration {
        font-size: 10px;
        font-weight: 500;
      }
      
      .audio-play {
        all: unset;
        cursor: pointer;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px;
        background-color: rgb(59, 59, 59);
        width: 12px;
        height: 12px;
        
        & svg {
          width: 100%;
          height: 100%;
        }
        
        &:hover {
          background-color: rgba(59, 59, 59, 0.8);
        }
      }
      
      .audio-actions {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
      
      .audio-play-icon {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `;
Bt([
  m()
], nt.prototype, "audio", 2);
Bt([
  H()
], nt.prototype, "isPlayingAudio", 2);
Bt([
  H()
], nt.prototype, "audioDuration", 2);
nt = Bt([
  V("audio-message")
], nt);
var Vs = ["second", "minute", "hour", "day", "week", "month", "year"];
function Rs(i, t) {
  if (t === 0)
    return ["just now", "right now"];
  var e = Vs[Math.floor(t / 2)];
  return i > 1 && (e += "s"), [i + " " + e + " ago", "in " + i + " " + e];
}
var Os = ["秒", "分钟", "小时", "天", "周", "个月", "年"];
function Hs(i, t) {
  if (t === 0)
    return ["刚刚", "片刻后"];
  var e = Os[~~(t / 2)];
  return [i + " " + e + "前", i + " " + e + "后"];
}
var fe = {}, ke = function(i, t) {
  fe[i] = t;
}, Us = function(i) {
  return fe[i] || fe.en_US;
}, oe = [
  60,
  60,
  24,
  7,
  365 / 7 / 12,
  12
];
function Ke(i) {
  return i instanceof Date ? i : !isNaN(i) || /^\d+$/.test(i) ? new Date(parseInt(i)) : (i = (i || "").trim().replace(/\.\d+/, "").replace(/-/, "/").replace(/-/, "/").replace(/(\d)T(\d)/, "$1 $2").replace(/Z/, " UTC").replace(/([+-]\d\d):?(\d\d)/, " $1$2"), new Date(i));
}
function Ds(i, t) {
  var e = i < 0 ? 1 : 0;
  i = Math.abs(i);
  for (var s = i, o = 0; i >= oe[o] && o < oe.length; o++)
    i /= oe[o];
  return i = Math.floor(i), o *= 2, i > (o === 0 ? 9 : 1) && (o += 1), t(i, o, s)[e].replace("%s", i.toString());
}
function Ts(i, t) {
  var e = t ? Ke(t) : /* @__PURE__ */ new Date();
  return (+e - +Ke(i)) / 1e3;
}
var Ns = function(i, t, e) {
  var s = Ts(i, e && e.relativeDate);
  return Ds(s, Us(t));
};
ke("en_US", Rs);
ke("zh_CN", Hs);
var Is = Object.defineProperty, Ps = Object.getOwnPropertyDescriptor, _e = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Ps(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Is(t, e, o), o;
};
let yt = class extends A {
  constructor() {
    super(...arguments), this.datetime = /* @__PURE__ */ new Date(), this.locale = "en";
  }
  render() {
    return u`
            <p>
                ${Ns(this.datetime, {
      en: "en_US",
      es: "es"
    }[this.locale])}
            </p>
        `;
  }
};
yt.styles = M`
      p {
        margin: 6px 0 0 0;
        color: rgb(160, 174, 192);
        font-size: 12px;
      }
      
    `;
_e([
  m()
], yt.prototype, "datetime", 2);
_e([
  m()
], yt.prototype, "locale", 2);
yt = _e([
  V("message-datetime")
], yt);
var js = Object.defineProperty, Bs = Object.getOwnPropertyDescriptor, Ee = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Bs(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && js(t, e, o), o;
};
let xt = class extends A {
  constructor() {
    super(...arguments), this.locale = "en";
  }
  onSelectedButton(i) {
    this.dispatchEvent(new CustomEvent("button-click", {
      detail: {
        index: i
      }
    }));
  }
  render() {
    const i = (this.message.buttons || []).some((t) => t.selected);
    return u`
            <div class="avatar">
                <img src="https://www.maiacognitive.com/wp-content/themes/maia/static/assets/img/icono-maia-maia1.png" alt="MAIA Logo"/>
            </div>
            <div>
                <div class="text-bubble">
                    ${this.message.text ? u`<p class="text-message">${this.message.text}</p>` : null}
                    ${this.message.buttons != null ? u`
                            <ul class=${Ct({ "button-list": !0, clean: !i })}>
                                ${(this.message.buttons || []).map((t, e) => u`<li class="button-list-item">
                                        <button class=${Ct({ button: !0, selected: t.selected })} @click="${() => this.onSelectedButton(e)}">
                                            <div class="button-indicator"></div>
                                            ${t.title}
                                        </button>
                                    </li>`)}
                            </ul>
                            ` : null}
                    ${this.message.image != null ? u`
                            <div class="img-wrapper">
                                <a href=${this.message.image} target="_blank">
                                    <img class="img" src=${this.message.image} alt="Chat image">      
                                </a>
                            </div>
                            ` : null}
                    ${this.message.audio != null ? u`
                                <audio-message audio=${this.message.audio}></audio-message>
                            ` : null}
                </div>
                <message-datetime datetime=${this.message.datetime} locale=${this.locale}></message-datetime>
            </div>
        `;
  }
};
xt.styles = M`
      p {
        margin: 0
      }

      :host {
        display: flex;
      }
      
      .avatar {
        background-color: rgb(226, 232, 240);
        width: 30px;
        height: 30px;
        padding: 8px;
        display: flex;
        border-radius: 50%;
        margin-right: 16px;
      }
      
      .text-bubble {
        background-color: rgb(226, 232, 240);
        padding: 10px;
        gap: 8px;
        border-radius: 0 8px 8px 8px;
        animation: show 0.2s linear;
      }

      @keyframes show{
        0% {
            opacity: 0.4;
            transform: translate(0, 10px);
        }
        100% {
            opacity: 1;
            transform: translate(0, 0px);
        }
      }
      
      .text-message {
        color: rgb(45, 55, 72);
        font-size: 14px;
        white-space: pre-wrap;
      }

      .button-list {
        all: unset; 
        display: flex;
        flex-direction: column;
        margin-top: 8px;
        width: 100%;
      }
      
      .button-list-item {
        all: unset;
        margin-bottom: 6px;
        width: 100%;
        
        &:last-child {
          margin-bottom: 0;
        }
      }
      
      .button {
        all: unset;
        cursor: pointer;
        background-color: white;
        display: flex;
        align-items: center;
        padding: 6px;
        width: 100%;
        box-sizing: border-box;
        border-radius: 4px;
        font-size: 14px;
        
        &:disabled {
          cursor: not-allowed;
        }
      }
      
      .button.selected .button-indicator, .button-list.clean .button:hover .button-indicator {
        background-color: #317B22; 
      }
      
      .button-indicator {
        margin-right: 4px;
        border-radius: 50%;
        width: 10px;
        height: 10px;
        background-color: rgb(226, 232, 240);
      }
      
      .img {
        width: 150px;
        height: 150px;
        object-fit: contain;
      }
      
      .img-wrapper {
        margin-top: 6px;
      }
    `;
Ee([
  m({ type: Object })
], xt.prototype, "message", 2);
Ee([
  m()
], xt.prototype, "locale", 2);
xt = Ee([
  V("bot-message")
], xt);
var Zs = Object.defineProperty, Gs = Object.getOwnPropertyDescriptor, Se = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Gs(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Zs(t, e, o), o;
};
let At = class extends A {
  constructor() {
    super(...arguments), this.locale = "en";
  }
  render() {
    let i = null;
    this.message.image != null && (i = u`
                <div class="attachment">
                    <svg class="attachment-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M23 4C23 2.34315 21.6569 1 20 1H4C2.34315 1 1 2.34315 1 4V20C1 21.6569 2.34315 23 4 23H20C21.6569 23 23 21.6569 23 20V4ZM21 4C21 3.44772 20.5523 3 20 3H4C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V4Z"
                                fill="#0F0F0F"
                            ></path>
                            <path
                                d="M4.80665 17.5211L9.1221 9.60947C9.50112 8.91461 10.4989 8.91461 10.8779 9.60947L14.0465 15.4186L15.1318 13.5194C15.5157 12.8476 16.4843 12.8476 16.8682 13.5194L19.1451 17.5039C19.526 18.1705 19.0446 19 18.2768 19H5.68454C4.92548 19 4.44317 18.1875 4.80665 17.5211Z"
                                fill="#0F0F0F"
                            ></path>
                            <path d="M18 8C18 9.10457 17.1046 10 16 10C14.8954 10 14 9.10457 14 8C14 6.89543 14.8954 6 16 6C17.1046 6 18 6.89543 18 8Z" fill="#0F0F0F"></path>
                        </g>
                    </svg>

                    <span class="attachment-text">${this.message.image.name}</span>
                </div>
            `);
    let t = null;
    this.message.location != null && (t = u`
                <div class="attachment">
                    <svg class="attachment-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                            <path d="M12 21C15.5 17.4 19 14.1764 19 10.2C19 6.22355 15.866 3 12 3C8.13401 3 5 6.22355 5 10.2C5 14.1764 8.5 17.4 12 21Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path d="M12 12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C10.8954 8 10 8.89543 10 10C10 11.1046 10.8954 12 12 12Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                        </g>
                    </svg>

                    <span class="attachment-text">${{
      en: "You shared your location",
      es: "Compartiste tu ubicación"
    }[this.locale]}</span>
                </div>
            `);
    let e = null;
    return this.message.audio != null && (e = u`
                <div class="attachment">
                    <svg class="attachment-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                            <path
                                d="M19 10V12C19 15.866 15.866 19 12 19M5 10V12C5 15.866 8.13401 19 12 19M12 19V22M8 22H16M12 15C10.3431 15 9 13.6569 9 12V5C9 3.34315 10.3431 2 12 2C13.6569 2 15 3.34315 15 5V12C15 13.6569 13.6569 15 12 15Z"
                                stroke="#000000"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            ></path>
                        </g>
                    </svg>


                    <span class="attachment-text">${{
      en: "You shared an audio",
      es: "Compartiste un audio"
    }[this.locale]}</span>
                </div>
            `), u`
            <div class="text-bubble-wrapper">
                <div class="text-bubble">
                    <p class="text-message">${this.message.text}</p>
                    ${i}
                    ${t}
                    ${e}
                </div>
                ${this.message.error ? u`
                        <div class="error-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 512 512">
                                <path d="M448,256c0-106-86-192-192-192S64,150,64,256s86,192,192,192S448,362,448,256Z" style="fill:none;stroke:#F25757;stroke-miterlimit:10;stroke-width:32px"/>
                                <path d="M250.26,166.05,256,288l5.73-121.95a5.74,5.74,0,0,0-5.79-6h0A5.74,5.74,0,0,0,250.26,166.05Z" style="fill:none;stroke:#F25757;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"/>
                                <path d="M256,367.91a20,20,0,1,1,20-20A20,20,0,0,1,256,367.91Z" style="fill:#F25757"/>
                            </svg>
                        </div>
                    ` : null}
            </div>

            ${this.message.error ? u`
                    <p class="error-text">Failed to send message</p>
                ` : u`
                    <message-datetime datetime=${this.message.datetime} locale=${this.locale}></message-datetime>
                `}
        `;
  }
};
At.styles = M`
      p {
        margin: 0
      }

      :host {
        display: flex;
        flex-direction: column;
        align-self: flex-end;
      }
      
      .text-bubble {
        display: flex;
        flex-direction: column;
        gap: 8px;
        background-color: rgb(49, 130, 206);
        padding: 10px;
        border-radius: 8px 0 8px 8px;
        animation: show 0.2s linear;
      }

      @keyframes show{
        0% {
            opacity: 0.4;
            transform: translate(0, 10px);
        }
        100% {
            opacity: 1;
            transform: translate(0, 0px);
        }
      }
      
      .text-message {
        color: rgba(255, 255, 255, 0.87);
        font-size: 14px;
        white-space: pre-wrap;
      }
      
      .error-icon {
        margin-left: 6px;
      }
      
      .error-text {
        margin-top: 4px;
        color: #F25757;
        font-size: 11px;
        font-weight: 500;
      }
      
      .text-bubble-wrapper {
        display: flex;
        align-self: flex-end;
        align-items: center;
      }
      
      .attachment {
        background-color: white;
        padding: 0.45rem 0.75rem;
        border-radius: 6px;
        display: flex;
        align-items: center;
        gap: 5px;
      }
      
      .attachment-icon {
        width: 22px;
        height: 22px;
      }
      
      .attachment-text {
        color: rgba(0, 0, 0, 0.87);
        font-size: 14px;
        font-style: italic;
      }
    `;
Se([
  m({ type: Object })
], At.prototype, "message", 2);
Se([
  m()
], At.prototype, "locale", 2);
At = Se([
  V("user-message")
], At);
var zs = Object.defineProperty, qs = Object.getOwnPropertyDescriptor, Fs = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? qs(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && zs(t, e, o), o;
};
let be = class extends A {
  render() {
    return u`          
            <div class="typing">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
            </div>
        `;
  }
};
be.styles = M`
        .typing {
          align-items: center;
          display: flex;
          height: 17px;
        }
        .typing .dot {
          animation: mercuryTypingAnimation 1.8s infinite ease-in-out;
          background-color: #1e1e1eb3; //rgba(30,30,30,.7);
          border-radius: 50%;
          height: 6px;
          margin-right: 4px;
          vertical-align: middle;
          width: 6px;
          display: inline-block;
        }
        .typing .dot:nth-child(1) {
          animation-delay: 200ms;
        }
        .typing .dot:nth-child(2) {
          animation-delay: 300ms;
        }
        .typing .dot:nth-child(3) {
          animation-delay: 400ms;
        }
        .typing .dot:last-child {
          margin-right: 0;
        }
        
        @keyframes mercuryTypingAnimation {
          0% {
            transform: translateY(0px);
            background-color: #1e1e1eb3; //rgba(30,30,30,.7);
          }
          28% {
            transform: translateY(-7px);
            background-color: #1e1e1e66; //rgba(30,30,30,.4);
          }
          44% {
            transform: translateY(0px);
            background-color: #1e1e1e66; //rgba(30,30,30,.2);
          }
        }
    `;
be = Fs([
  V("chat-typing")
], be);
var Xs = Object.defineProperty, Qs = Object.getOwnPropertyDescriptor, Ws = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Qs(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Xs(t, e, o), o;
};
let we = class extends A {
  render() {
    return u`         
            <div class="avatar">
                <img src="https://www.maiacognitive.com/wp-content/themes/maia/static/assets/img/icono-maia-maia1.png" alt="MAIA Logo"/>
            </div>
            <div class="text-bubble">
                <chat-typing></chat-typing>
            </div>
        `;
  }
};
we.styles = M`
      :host {
        display: flex;
      }
      
      .avatar {
        background-color: rgb(226, 232, 240);
        width: 30px;
        height: 30px;
        padding: 8px;
        display: flex;
        border-radius: 50%;
        margin-right: 16px;
      }
      
      .text-bubble {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgb(226, 232, 240);
        padding: 10px;
        border-radius: 0 8px 8px 8px;
        animation: show 0.2s linear;
      }
    `;
we = Ws([
  V("bot-message-typing")
], we);
var Ys = Object.defineProperty, Js = Object.getOwnPropertyDescriptor, kt = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Js(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && Ys(t, e, o), o;
};
let Y = class extends A {
  constructor() {
    super(...arguments), this.wrapperRef = Dt(), this.messages = [], this.botIsTyping = !1, this.locale = "en", this.resetScrollButtonVisible = !1;
  }
  onScrollEnd() {
    const { offsetHeight: i, scrollHeight: t, scrollTop: e } = this.wrapperRef.value;
    this.resetScrollButtonVisible = t > e + i + 100;
  }
  onScroll() {
    this.scrollTimeout != null && clearTimeout(this.scrollTimeout), this.scrollTimeout = setTimeout(this.onScrollEnd.bind(this), 250);
  }
  scrollToEnd() {
    var i;
    const { scrollHeight: t } = this.wrapperRef.value;
    (i = this.wrapperRef.value) == null || i.scrollTo({ top: t, left: 0, behavior: "smooth" });
  }
  async getUpdateComplete() {
    await super.getUpdateComplete();
    const i = Array.from(this.shadowRoot ? this.shadowRoot.querySelectorAll("user-message") : []), t = Array.from(this.shadowRoot ? this.shadowRoot.querySelectorAll("bot-message") : []), e = i.map((o) => o.updateComplete), s = t.map((o) => o.updateComplete);
    return await Promise.all(e.concat(s)), !0;
  }
  async updated(i) {
    var t;
    super.updated(i);
    const { offsetHeight: e, scrollHeight: s, scrollTop: o } = this.wrapperRef.value;
    let n = s <= o + e + 100;
    await this.updateComplete, n && ((t = this.wrapperRef.value) == null || t.scrollTo({ top: s, left: 0, behavior: "smooth" }));
  }
  onButtonSelected(i, t) {
    this.dispatchEvent(new CustomEvent("button-click", {
      detail: {
        "button-index": i,
        "message-index": t
      }
    }));
  }
  render() {
    return u`
            <div class="wrapper" ${Tt(this.wrapperRef)} @scroll=${this.onScroll}>
                ${this.messages.map((i, t) => {
      if (i.isUser) {
        const e = i.message;
        return u`
                            <user-message 
                                    .message=${e} 
                                    locale=${this.locale}
                            >
                            </user-message>`;
      } else {
        const e = i.message;
        return u`
                            <bot-message 
                                    .message=${e}
                                    locale=${this.locale}
                                    @button-click=${(s) => {
          this.onButtonSelected(s.detail.index, t);
        }}
                            >
                            </bot-message>`;
      }
    })}
                ${this.botIsTyping ? u`<bot-message-typing></bot-message-typing>` : null}
                ${this.resetScrollButtonVisible ? u`
                        <button @click=${this.scrollToEnd.bind(this)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">   
                                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
                            </svg>
        
                        </button>
                    ` : u``}
            </div>
        `;
  }
};
Y.styles = M`
      :host {
        height: 400px;
        width: 100%;  
        position: relative;
      }
      
      .wrapper {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        width: 100%;
        height: 100%;
        padding: 15px;
        box-sizing: border-box;
        overflow: auto;
        gap: 16px;
      }
      
      button {
        background-color: rgb(255, 255, 255);
        width: 45px;
        height: 45px;
        border: none;
        box-sizing: border-box;
        padding: 12px;
        position: absolute;
        margin-left: auto;
        margin-right: auto;
        bottom: 15px;
        left: 0;
        right: 0;
        border-radius: 50%;
        cursor: pointer;
        color: rgb(45, 55, 72);
        box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);;
      }
    `;
kt([
  m({ type: Array })
], Y.prototype, "messages", 2);
kt([
  m({ type: Boolean, attribute: "bot-is-typing" })
], Y.prototype, "botIsTyping", 2);
kt([
  m()
], Y.prototype, "locale", 2);
kt([
  H()
], Y.prototype, "resetScrollButtonVisible", 2);
Y = kt([
  V("maia-message-list")
], Y);
class Ks {
  constructor(t) {
    this.domain = t;
  }
  sendText(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    e == null && (e = E()), s == null && (s = E());
    const o = new FormData();
    return o.append("text", t), o.append("type", "text"), o.append("id_message", s), o.append("id_chat", e), fetch(new URL("/webhooks/chatweb_connector/webhook", this.domain).href, {
      method: "POST",
      body: o
    }).then((n) => n.json());
  }
  sendImage(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    e == null && (e = E()), s == null && (s = E());
    const o = new FormData();
    return o.append("image", t), o.append("type", "image"), o.append("id_message", s), o.append("id_chat", e), fetch(new URL("/webhooks/chatweb_connector/webhook", this.domain).href, {
      method: "POST",
      body: o
    }).then((n) => n.json());
  }
  sendLocation(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    e == null && (e = E()), s == null && (s = E());
    const o = new FormData();
    return o.append("latitude", t.latitude.toString()), o.append("longitude", t.longitude.toString()), o.append("type", "location"), o.append("id_message", s), o.append("id_chat", e), fetch(new URL("/webhooks/chatweb_connector/webhook", this.domain).href, {
      method: "POST",
      body: o
    }).then((n) => n.json());
  }
  sendAudio(t, { conversationID: e = void 0, messageID: s = void 0 } = {}) {
    e == null && (e = E()), s == null && (s = E());
    const o = new FormData();
    return o.append("audio", t), o.append("type", "audio"), o.append("id_message", s), o.append("id_chat", e), fetch(new URL("/webhooks/chatweb_connector/webhook", this.domain).href, {
      method: "POST",
      body: o
    }).then((n) => n.json());
  }
}
var to = Object.defineProperty, eo = Object.getOwnPropertyDescriptor, D = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? eo(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && to(t, e, o), o;
};
let S = class extends A {
  constructor() {
    super(...arguments), this.conversationID = E(), this.conversationManagerToken = "", this.locale = "en", this.isWaitingResponse = !0, this.isOpenEndChat = !1, this.messages = [];
  }
  connectedCallback() {
    if (super.connectedCallback(), this.conversationManagerUrl)
      this.service = new fs(this.conversationManagerUrl, this.conversationManagerToken);
    else if (this.rasaDomain)
      this.service = new Ks(this.rasaDomain);
    else
      throw new Error("Undefined conversation-manager-domain / rasa-domain");
    this.sendInitialMessageAndUpdateUI();
  }
  sendInitialMessageAndUpdateUI() {
    this.initialMessage != null && this.service.sendText(this.initialMessage, { conversationID: this.conversationID }).then((i) => {
      for (let t of i)
        this.messages.push({
          isUser: !1,
          message: {
            text: t.custom.text,
            datetime: /* @__PURE__ */ new Date(),
            buttons: t.custom.buttons,
            image: t.custom.image,
            audio: t.custom.audio
          }
        });
      this.requestUpdate();
    }).catch((i) => {
      console.error(i);
    }).finally(() => {
      this.isWaitingResponse = !1;
    });
  }
  sendMessageAndUpdateUI(i, t) {
    const e = this.messages.push({
      isUser: !0,
      message: t
    });
    this.requestUpdate();
    const s = e - 1;
    this.isWaitingResponse = !0, i.then((o) => {
      for (let n of o)
        this.messages.push({
          isUser: !1,
          message: {
            text: n.custom.text,
            datetime: /* @__PURE__ */ new Date(),
            buttons: n.custom.buttons,
            image: n.custom.image,
            audio: n.custom.audio
          }
        });
      this.requestUpdate();
    }).catch((o) => {
      console.error(o);
      const n = this.messages[s].message;
      n.error = !0, this.requestUpdate();
    }).finally(() => {
      this.isWaitingResponse = !1;
    });
  }
  minimizeChat() {
    this.dispatchEvent(new CustomEvent("minimize"));
  }
  closeChat() {
    this.dispatchEvent(new CustomEvent("close"));
  }
  cancelEndChat() {
    this.isOpenEndChat = !1;
  }
  askForCloseChat() {
    this.isOpenEndChat = !0;
  }
  onUserMessage(i) {
    this.sendMessageAndUpdateUI(
      this.service.sendText(i.detail.text, { conversationID: this.conversationID }),
      {
        text: i.detail.text,
        error: !1,
        datetime: /* @__PURE__ */ new Date()
      }
    );
  }
  onMessageButtonClick(i) {
    const t = i.detail["message-index"], e = i.detail["button-index"];
    if (t !== this.messages.length - 1)
      return;
    const s = this.messages[t].message.buttons[e];
    s.selected = !0, this.requestUpdate(), this.sendMessageAndUpdateUI(
      this.service.sendText(s.payload, { conversationID: this.conversationID }),
      {
        text: s.title,
        error: !1,
        datetime: /* @__PURE__ */ new Date()
      }
    );
  }
  onUserImage(i) {
    this.sendMessageAndUpdateUI(
      this.service.sendImage(i.detail.image, { conversationID: this.conversationID }),
      {
        image: {
          name: i.detail.image.name
        },
        error: !1,
        datetime: /* @__PURE__ */ new Date()
      }
    );
  }
  onUserLocation(i) {
    this.sendMessageAndUpdateUI(
      this.service.sendLocation(i.detail.location, { conversationID: this.conversationID }),
      {
        location: {
          latitude: i.detail.latitude,
          longitude: i.detail.longitude
        },
        error: !1,
        datetime: /* @__PURE__ */ new Date()
      }
    );
  }
  onUserAudio(i) {
    this.sendMessageAndUpdateUI(
      this.service.sendAudio(i.detail.audio, { conversationID: this.conversationID }),
      {
        audio: {},
        error: !1,
        datetime: /* @__PURE__ */ new Date()
      }
    );
  }
  render() {
    if (!["en", "es"].includes(this.locale))
      throw new Error(`Unexpected locale ${this.locale}. Available locale: ${this.locale}`);
    return u`
            <div class="chat-header">
              <div class="chat-header-btns">
                <button class="chat-header-button" @click=${this.minimizeChat.bind(this)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 512 512">
                        <line x1="400" y1="256" x2="112" y2="256" style="fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"/>
                    </svg>
                </button>
                <button class="chat-header-button" @click=${this.askForCloseChat.bind(this)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 512 512">
                        <line x1="368" y1="368" x2="144" y2="144" style="fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"/>
                        <line x1="368" y1="144" x2="144" y2="368" style="fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;stroke-width:32px"/>
                    </svg>
                </button>
              </div>
            </div>
            <maia-message-list
              messages=${JSON.stringify(this.messages)}
              @button-click=${this.onMessageButtonClick.bind(this)}
              locale=${this.locale}
              ?bot-is-typing=${this.isWaitingResponse}
            ></maia-message-list>
            <maia-chat-input
                @user-text=${this.onUserMessage.bind(this)}
                @user-image=${this.onUserImage.bind(this)}
                @user-location=${this.onUserLocation.bind(this)}
                @user-audio=${this.onUserAudio.bind(this)}
                locale=${this.locale}
            >
            </maia-chat-input>
                  
            ${this.isOpenEndChat ? u`
                    <div class="close-modal-wrapper">
                        <div class="close-modal">
                            <p>Are you sure you want to end the conversation?</p>
                            <div class="close-modal-buttons">
                                <button class="end-chat-button" @click=${this.closeChat.bind(this)}>End</button>
                                <button class="cancel-button" @click=${this.cancelEndChat.bind(this)}>Cancel</button>
                            </div>
                        </div>
                    </div>` : null}
            `;
  }
};
S.styles = M`
      :host {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 350px;
        background-color: rgba(255, 255, 255, 1);
        box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        
      }
      
      .chat-header {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 40px;
        border-radius: 10px 10px 0 0;
        color: white;
        padding-left:10px;
        padding-right:10px;
        background-color: rgb(226, 232, 240);
      }

      .chat-header-btns {
        display: flex;
        align-items: center;
      }

      .chat-header img {
        height: 25px;
        padding: 2px;
      }

      .chat-avatar {
        position: fixed;
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      }
      .avatar-image {
        max-width: 20%;
        height: auto;
      }
      .chat-header-button {
        all: unset;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        font-size: 22px;
        color: rgb(59, 59, 59);
        padding: 6px;
        border-radius: 10px;
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.36);  
        }
        
        &:first-child {
          margin-right: 10px;  
        }
      }

      .close-modal-wrapper {
        overflow: hidden;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.2);
        z-index: 3;
        border-radius: 10px; 
      }
      
      .close-modal {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        font-size: 1rem;
        text-align: center;
        background-color: white;
        padding:10px;
        border-radius: 10px;
        animation: show 0.3s linear;
      }

      @keyframes show{
        0% {
            opacity: 0.4;
            transform: translate(0, 100px);
        }
        100% {
            opacity: 1;
            transform: translate(0, 0px);
        }
      }
    
      .close-modal p {
        margin-bottom: 1rem;
      }
    
      .close-modal-buttons {
        display: block;
        justify-content: center;
      }
    
      .end-chat-button,
      .cancel-button {
        margin-bottom:10px;
        border: 0px;
        padding: 0.5rem 1rem;
        border-radius:5px;
        width:100%;
        font-size: 14px;
      }

      .end-chat-button {
        color:white;
        background-color:red;
      }
    `;
D([
  m({ attribute: "conversation-manager-url" })
], S.prototype, "conversationManagerUrl", 2);
D([
  m({ attribute: "conversation-manager-token" })
], S.prototype, "conversationManagerToken", 2);
D([
  m({ attribute: "rasa-domain" })
], S.prototype, "rasaDomain", 2);
D([
  m({ attribute: "initial-message" })
], S.prototype, "initialMessage", 2);
D([
  m()
], S.prototype, "locale", 2);
D([
  H()
], S.prototype, "isWaitingResponse", 2);
D([
  H()
], S.prototype, "isOpenEndChat", 2);
D([
  H()
], S.prototype, "messages", 2);
S = D([
  V("maia-chat")
], S);
var io = Object.defineProperty, so = Object.getOwnPropertyDescriptor, T = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? so(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && io(t, e, o), o;
};
let L = class extends A {
  constructor() {
    super(...arguments), this.conversationManagerToken = "", this.locale = "en", this.logo = "data:image/png;base64,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", this.isOpen = !1, this.isHidden = !1;
  }
  openChat() {
    this.isHidden = !1, this.isOpen = !0;
  }
  hideChat() {
    this.isOpen = !0, this.isHidden = !0;
  }
  closeChat() {
    this.isOpen = !1, this.isHidden = !1;
  }
  render() {
    return u`
            ${this.isHidden || !this.isOpen ? u`
                    <button class="open-btn" @click=${this.openChat.bind(this)}>
                        <img class="open-btn-img" src=${this.logo} alt="Logo">
                    </button>  
                ` : null}
            ${this.isOpen ? u`
                    <div class=${Ct({ hidden: this.isHidden })}>
                        <maia-chat 
                            conversation-manager-url=${this.conversationManagerUrl} 
                            conversation-manager-token=${this.conversationManagerToken} 
                            rasa-domain=${this.rasaDomain}
                            initial-message=${this.initialMessage}
                            locale=${this.locale}
                            @close=${this.closeChat.bind(this)}
                            @minimize=${this.hideChat.bind(this)}
                        ></maia-chat>
                    </div>           
                ` : null}
        `;
  }
};
L.styles = M`
      .open-btn {
        all: unset;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #fff;
        border: none;
        border-radius: 45px;
        box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease 0s;
        cursor: pointer;
        outline: none;
        width: 80px;
        height: 80px;
        
        &:hover {
          background: var(--maia-chat-widget-hover-background, #3ECCB4);
          box-shadow: 0 15px 20px rgba(46, 229, 157, 0.4);
          transform: translateY(-7px);
        }
      }
      
      .open-btn-img {
        width: 75%;
        height: 75%;
      }
      
      .hidden {
        display: none;
      }
      
      #wrapper {
        padding: 0.5rem;
        border-radius: 50%;
        border: 1px solid red;
        background-color: white;
      }
    `;
T([
  m({ attribute: "conversation-manager-url" })
], L.prototype, "conversationManagerUrl", 2);
T([
  m({ attribute: "conversation-manager-token" })
], L.prototype, "conversationManagerToken", 2);
T([
  m({ attribute: "rasa-domain" })
], L.prototype, "rasaDomain", 2);
T([
  m()
], L.prototype, "locale", 2);
T([
  m()
], L.prototype, "logo", 2);
T([
  m({ attribute: "initial-message" })
], L.prototype, "initialMessage", 2);
T([
  H()
], L.prototype, "isOpen", 2);
T([
  H()
], L.prototype, "isHidden", 2);
L = T([
  V("maia-chat-widget")
], L);
ke("es", (i, t, e) => [
  ["justo ahora", "en un rato"],
  ["hace %s segundos", "en %s segundos"],
  ["hace 1 minuto", "en 1 minuto"],
  ["hace %s minutos", "en %s minutos"],
  ["hace 1 hora", "en 1 hora"],
  ["hace %s horas", "en %s horas"],
  ["hace 1 día", "en 1 día"],
  ["hace %s días", "en %s días"],
  ["hace 1 semana", "en 1 semana"],
  ["hace %s semanas", "en %s semanas"],
  ["hace 1 mes", "en 1 mes"],
  ["hace %s meses", "en %s meses"],
  ["hace 1 año", "en 1 año"],
  ["hace %s años", "en %s años"]
][t]);
class oo extends Error {
  constructor(t) {
    super(), this.response = t;
  }
}
function ti(i) {
  if (!i.ok)
    throw new oo(i);
  return i.json();
}
class no {
  constructor(t) {
    this.domain = t;
  }
  async sendText(t, e) {
    const s = new FormData();
    return s.append("text", t), s.append("id_chat", e), fetch(this.domain + "/webhooks/videochat_connector/webhook", {
      method: "POST",
      body: s
    }).then(ti);
  }
  async sendAudio(t, e) {
    const s = new FormData();
    s.append("audio", t), s.append("id_chat", e);
    const o = await fetch(this.domain + "/webhooks/videochat_connector/webhook", {
      method: "POST",
      body: s
    });
    return ti(o).then((n) => ({
      response: n,
      transcription: o.headers.get("X-Transcription")
    }));
  }
}
function ei() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}
class ro {
  constructor(t) {
    this.maxSize = t, this.queue = [];
  }
  push(t) {
    this.queue.length >= this.maxSize && this.queue.shift(), this.queue.push(t);
  }
  size() {
    return this.queue.length;
  }
  reset() {
    this.queue = [];
  }
  get() {
    return this.queue;
  }
}
function ao() {
  return /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const lo = (i) => i.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const gt = (i, t) => {
  var e, s;
  const o = i._$AN;
  if (o === void 0)
    return !1;
  for (const n of o)
    (s = (e = n)._$AO) === null || s === void 0 || s.call(e, t, !1), gt(n, t);
  return !0;
}, Nt = (i) => {
  let t, e;
  do {
    if ((t = i._$AM) === void 0)
      break;
    e = t._$AN, e.delete(i), i = t;
  } while ((e == null ? void 0 : e.size) === 0);
}, Ai = (i) => {
  for (let t; t = i._$AM; i = t) {
    let e = t._$AN;
    if (e === void 0)
      t._$AN = e = /* @__PURE__ */ new Set();
    else if (e.has(i))
      break;
    e.add(i), po(t);
  }
};
function ho(i) {
  this._$AN !== void 0 ? (Nt(this), this._$AM = i, Ai(this)) : this._$AM = i;
}
function co(i, t = !1, e = 0) {
  const s = this._$AH, o = this._$AN;
  if (o !== void 0 && o.size !== 0)
    if (t)
      if (Array.isArray(s))
        for (let n = e; n < s.length; n++)
          gt(s[n], !1), Nt(s[n]);
      else
        s != null && (gt(s, !1), Nt(s));
    else
      gt(this, i);
}
const po = (i) => {
  var t, e, s, o;
  i.type == hi.CHILD && ((t = (s = i)._$AP) !== null && t !== void 0 || (s._$AP = co), (e = (o = i)._$AQ) !== null && e !== void 0 || (o._$AQ = ho));
};
class uo extends pi {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(t, e, s) {
    super._$AT(t, e, s), Ai(this), this.isConnected = t._$AU;
  }
  _$AO(t, e = !0) {
    var s, o;
    t !== this.isConnected && (this.isConnected = t, t ? (s = this.reconnected) === null || s === void 0 || s.call(this) : (o = this.disconnected) === null || o === void 0 || o.call(this)), e && (gt(this, t), Nt(this));
  }
  setValue(t) {
    if (lo(this._$Ct))
      this._$Ct._$AI(t, this);
    else {
      const e = [...this._$Ct._$AH];
      e[this._$Ci] = t, this._$Ct._$AI(e, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ct = () => new go();
class go {
}
const ne = /* @__PURE__ */ new WeakMap(), pt = ci(class extends uo {
  render(i) {
    return f;
  }
  update(i, [t]) {
    var e;
    const s = t !== this.G;
    return s && this.G !== void 0 && this.ot(void 0), (s || this.rt !== this.lt) && (this.G = t, this.dt = (e = i.options) === null || e === void 0 ? void 0 : e.host, this.ot(this.lt = i.element)), f;
  }
  ot(i) {
    var t;
    if (typeof this.G == "function") {
      const e = (t = this.dt) !== null && t !== void 0 ? t : globalThis;
      let s = ne.get(e);
      s === void 0 && (s = /* @__PURE__ */ new WeakMap(), ne.set(e, s)), s.get(this.G) !== void 0 && this.G.call(this.dt, void 0), s.set(this.G, i), i !== void 0 && this.G.call(this.dt, i);
    } else
      this.G.value = i;
  }
  get rt() {
    var i, t, e;
    return typeof this.G == "function" ? (t = ne.get((i = this.dt) !== null && i !== void 0 ? i : globalThis)) === null || t === void 0 ? void 0 : t.get(this.G) : (e = this.G) === null || e === void 0 ? void 0 : e.value;
  }
  disconnected() {
    this.rt === this.lt && this.ot(void 0);
  }
  reconnected() {
    this.ot(this.lt);
  }
});
var vo = Object.defineProperty, mo = Object.getOwnPropertyDescriptor, j = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? mo(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && vo(t, e, o), o;
};
let O = class extends R {
  constructor() {
    super(...arguments), this.micMuted = !1, this.soundMuted = !1, this.soundDisabled = !1, this.fullscreen = !1, this.raised = !1, this.chatOpen = !1, this.raiseDisabled = !1;
  }
  onMic() {
    this.dispatchEvent(new CustomEvent("mic"));
  }
  onRaise() {
    this.dispatchEvent(new CustomEvent("raise"));
  }
  onFullscreen() {
    this.dispatchEvent(new CustomEvent("fullscreen"));
  }
  onLeave() {
    this.dispatchEvent(new CustomEvent("leave"));
  }
  onSound() {
    this.dispatchEvent(new CustomEvent("sound"));
  }
  onChat() {
    this.dispatchEvent(new CustomEvent("chat"));
  }
  render() {
    const i = g`
            <img src="https://storage.googleapis.com/maia-videochat/logo-maia-header.svg" alt="Logo" id="logo">
        `;
    return g`
            ${i}
            
            <ul id="actions-list">
                <li class="action-list-item">
                    <button class="action-btn" @click=${this.onSound.bind(this)} ?disabled=${this.soundDisabled} id="sound-btn">
                        ${this.soundMuted ? g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier"><path d="M16 9L22 15M22 9L16 15M13 3L7 8H5C3.89543 8 3 8.89543 3 10V14C3 15.1046 3.89543 16 5 16H7L13 21V3Z" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></g>
                                </svg>

                                <span>Unsilence</span>
                            ` : g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        <path
                                            d="M16 9C16.5 9.5 17 10.5 17 12C17 13.5 16.5 14.5 16 15M19 6C20.5 7.5 21 10 21 12C21 14 20.5 16.5 19 18M13 3L7 8H5C3.89543 8 3 8.89543 3 10V14C3 15.1046 3.89543 16 5 16H7L13 21V3Z"
                                            stroke="#ffffff"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></path>
                                    </g>
                                </svg>
                                <span>Silence</span>
                                `}
                    </button>
                </li>
                <li class="action-list-item">
                    <button class="action-btn" @click=${this.onMic.bind(this)}>
                        ${this.micMuted ? g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                                    <g id="SVGRepo_iconCarrier"> <g clip-path="url(#clip0_1348_125844)"> <path fill-rule="evenodd" clip-rule="evenodd" d="M16 9.41421L23.7071 1.70711C24.0976 1.31658 24.0976 0.683417 23.7071 0.292893C23.3166 -0.0976311 22.6834 -0.0976311 22.2929 0.292893L0.292893 22.2929C-0.0976311 22.6834 -0.0976311 23.3166 0.292893 23.7071C0.683417 24.0976 1.31658 24.0976 1.70711 23.7071L7.0946 18.3196C8.2281 19.1995 9.57818 19.7585 11.0019 19.9375C11.0007 19.9582 11 19.979 11 20V22C11 22.5523 11.4477 23 12 23C12.5523 23 13 22.5523 13 22V20C13 19.979 12.9993 19.9582 12.9981 19.9375C14.7518 19.717 16.3938 18.9199 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12V10C20 9.44771 19.5523 9 19 9C18.4477 9 18 9.44771 18 10V12C18 13.5913 17.3679 15.1174 16.2426 16.2426C15.1174 17.3679 13.5913 18 12 18C10.746 18 9.53253 17.6075 8.52379 16.8904L9.96817 15.446C10.5637 15.7978 11.2589 16 12 16C14.2091 16 16 14.2091 16 12V9.41421ZM14 11.4142L11.4818 13.9324C11.6469 13.9765 11.8206 14 12 14C13.1046 14 14 13.1046 14 12V11.4142Z" fill="#ffffff"/> <path d="M8 5C8 2.79086 9.79086 1 12 1C13.983 1 15.6272 2.44198 15.9447 4.33454C16.0361 4.87922 15.6686 5.39484 15.124 5.48622C14.5793 5.5776 14.0637 5.21013 13.9723 4.66546C13.8137 3.72031 12.99 3 12 3C10.8954 3 10 3.89543 10 5V10.5C10 11.0523 9.55229 11.5 9 11.5C8.44771 11.5 8 11.0523 8 10.5V5Z" fill="#ffffff"/> <path d="M6 10C6 9.44771 5.55228 9 5 9C4.44772 9 4 9.44771 4 10V12C4 12.7811 4.11424 13.5505 4.33348 14.2857C4.49129 14.815 5.04827 15.1161 5.57752 14.9583C6.10678 14.8005 6.4079 14.2435 6.25009 13.7143C6.08568 13.1629 6 12.5859 6 12V10Z" fill="#ffffff"/> </g> <defs> <clipPath id="clip0_1348_125844"> <rect width="24" height="24" fill="white"/> </clipPath> </defs> </g>
                                </svg>
                                <span>Unmute</span>
                            ` : g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                                    <g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M12 1C9.79086 1 8 2.79086 8 5V12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12V5C16 2.79086 14.2091 1 12 1ZM10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5V12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12V5Z" fill="#ffffff"/> <path d="M5 9C5.55228 9 6 9.44771 6 10V12C6 13.5913 6.63214 15.1174 7.75736 16.2426C8.88258 17.3679 10.4087 18 12 18C13.5913 18 15.1174 17.3679 16.2426 16.2426C17.3679 15.1174 18 13.5913 18 12V10C18 9.44771 18.4477 9 19 9C19.5523 9 20 9.44771 20 10V12C20 14.1217 19.1571 16.1566 17.6569 17.6569C16.3938 18.9199 14.7518 19.717 12.9981 19.9375C12.9993 19.9582 13 19.979 13 20V22C13 22.5523 12.5523 23 12 23C11.4477 23 11 22.5523 11 22V20C11 19.979 11.0006 19.9582 11.0019 19.9375C9.2482 19.717 7.60623 18.9199 6.34315 17.6569C4.84285 16.1566 4 14.1217 4 12V10C4 9.44771 4.44772 9 5 9Z" fill="#ffffff"/> </g>
                                </svg>
                                <span>Mute</span>
                                `}
                    </button>
                </li>
                <li class="action-list-item">
                    <button class="action-btn" @click=${this.onRaise.bind(this)} ?disabled=${this.raiseDisabled}>
                        ${this.raised ? g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier"> <path d="M10 4.25C10 3.69772 9.55228 3.25 9 3.25C8.44772 3.25 8 3.69772 8 4.25V10.9982C8 11.2743 7.77614 11.4982 7.5 11.4982C7.22386 11.4982 7 11.2743 7 10.9982V5.99683C7 5.44454 6.55228 4.99683 6 4.99683C5.44772 4.99683 5 5.44454 5 5.99683V14.7536C5 16.933 6.16842 19.263 6.91404 20.5425C7.47017 21.4969 8.49409 22.0013 9.53657 22.0013H11.7046C12.9358 22.0013 14.0613 21.3056 14.6118 20.2043L14.7451 19.9374C15.1658 19.0957 15.6904 18.31 16.3067 17.5989L18.5186 15.0468L20.7105 13.342C20.8931 13.1999 21 12.9814 21 12.75C21 12.2651 20.7409 11.9051 20.4084 11.6903C20.1123 11.499 19.7584 11.4172 19.4464 11.3802C18.8102 11.3047 18.0364 11.3818 17.3571 11.5137C16.8436 11.6134 16.3829 11.8077 16 12.0182V4.25C16 3.69772 15.5523 3.25 15 3.25C14.4477 3.25 14 3.69772 14 4.25V10.5C14 10.7761 13.7761 11 13.5 11C13.2239 11 13 10.7761 13 10.5V2.99683C13 2.44454 12.5523 1.99683 12 1.99683C11.4477 1.99683 11 2.44454 11 2.99683V10.5C11 10.7761 10.7761 11 10.5 11C10.2239 11 10 10.7761 10 10.5V4.25Z" fill="#ffffff"></path></g>
                                </svg>
                                <span>Lower</span>
                                ` : g`
                                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#000000">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                                    <g id="SVGRepo_iconCarrier"> <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="ic_fluent_hand_raise_24_regular" fill="#ffffff" fill-rule="nonzero"> <path d="M11.4974,1.99706871 C12.3600875,1.99640433 13.0354801,2.50970554 13.3328276,3.19889673 L13.3872,3.33898 L13.5301172,3.29970656 C13.6270391,3.27692094 13.7271797,3.26103656 13.8301266,3.25270094 L13.9866,3.24594 L14.1385062,3.25087043 C15.1381001,3.32205511 15.9184637,4.14871006 15.9940011,5.09901621 L16,5.25 L16,11.2581 C16.3849,11.0467 16.8491,10.8542 17.3678,10.7632 C18.1185,10.6317 18.9851,10.6766 19.7008,11.0463 C20.4687,11.4429 21,12.1883 21,13.25 C21,13.4521143 20.9185224,13.6440898 20.7766006,13.784049 L20.7007,13.8495 L18.7428,15.3214 L16.6048,17.4572 C15.7846,18.2766 15.1021,19.2232 14.5838,20.2603 C14.0801278,21.2680222 13.0795361,21.9251981 11.9649896,21.9951074 L11.7672,22.0013 L9.03943,22.0013 C7.99781,22.0013 6.97463,21.4977 6.4182,20.5446 C5.70695238,19.3263143 4.61059819,17.1538336 4.50779631,15.0660089 L4.5,14.7536 L4.5,7 C4.5,5.95905 5.33182,4.99994 6.50115,4.99994 C6.6801,4.99994 6.85127,5.02235 7.01285,5.06407 C7.10363,4.10157 7.8915,3.24592 9,3.24592 C9.21687,3.24592 9.42187,3.27859 9.61197,3.33844 C9.87538,2.58304 10.5817,1.99778 11.4974,1.99706871 Z M11.4986,3.49706968 C11.2121,3.49729 11,3.72415 11,3.99682 L11,10.2494 L10.9931531,10.3512005 C10.9434889,10.7174146 10.6296833,11 10.25,11 C9.8703075,11 9.55650958,10.7178347 9.50684668,10.3517677 L9.5,10.25 L9.5,5.25 C9.5,4.9715 9.28688,4.74592 9,4.74592 C8.71851,4.74592 8.50409,4.97174 8.50409,5.25 L8.50409,10.7451 L8.49724332,10.8468736 C8.44758042,11.2129826 8.1337825,11.4954 7.75409,11.4954 C7.3743975,11.4954 7.06059958,11.2132347 7.01093668,10.8471677 L7.00409,10.7454 L7.00409,7 C7.00409,6.73065 6.79011,6.49994 6.50115,6.49994 C6.21505,6.49994 6,6.73158 6,7 L6,14.7536 C6,16.5004 6.97783,18.528 7.7136,19.7883 C7.97061,20.2285 8.46764,20.5013 9.03943,20.5013 L11.7672,20.5013 C12.3918,20.5013 12.9628,20.1484 13.242,19.5897 C13.8326,18.4081 14.6101,17.3296 15.5447,16.396 L17.7199,14.223 C17.7448,14.1982 17.7713,14.1752 17.7993,14.1541 L19.4577,12.9073 C19.3843,12.644 19.221,12.4867 19.0124,12.379 C18.6737,12.204 18.1641,12.1466 17.6268,12.2407 C17.1332,12.3272 16.6758,12.5737 16.3241,12.8227 C16.1521,12.9445 16.0151,13.06 15.9226,13.1434 L15.7952,13.2651 C15.5846,13.488 15.2593,13.5603 14.974,13.4474 C14.6879,13.3342 14.5,13.0577 14.5,12.75 L14.5,5.25 C14.5,4.99047 14.2584,4.7443 13.9959,4.74591 C13.7023,4.74762 13.4981,4.98399 13.4981,5.22345 L13.4981,10.2494 L13.4912531,10.3512005 C13.4415889,10.7174146 13.1277833,11 12.7481,11 C12.3684167,11 12.0546111,10.7178347 12.0049469,10.3517677 L11.9981,10.25 L11.9981,3.99682 C11.9981,3.72007 11.789,3.49685 11.4986,3.49706968 Z" id="🎨-Color"> </path> </g> </g> </g>
                                </svg>
                                <span>Raise</span>
                            `}
                    </button>
                </li>
                <li class="action-list-item">
                    <button class="action-btn" @click=${this.onChat.bind(this)}>
                        ${this.chatOpen ? g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.6288 20.4718L13.0867 21.3877C12.6035 22.204 11.3965 22.204 10.9133 21.3877L10.3712 20.4718C9.95073 19.7614 9.74049 19.4063 9.40279 19.2098C9.06509 19.0134 8.63992 19.0061 7.78958 18.9915C6.53422 18.9698 5.74689 18.8929 5.08658 18.6194C3.86144 18.1119 2.88807 17.1386 2.3806 15.9134C2 14.9946 2 13.8297 2 11.5V10.5C2 7.22657 2 5.58985 2.7368 4.38751C3.14908 3.71473 3.71473 3.14908 4.38751 2.7368C5.58985 2 7.22657 2 10.5 2H13.5C16.7734 2 18.4101 2 19.6125 2.7368C20.2853 3.14908 20.8509 3.71473 21.2632 4.38751C22 5.58985 22 7.22657 22 10.5V11.5C22 13.8297 22 14.9946 21.6194 15.9134C21.1119 17.1386 20.1386 18.1119 18.9134 18.6194C18.2531 18.8929 17.4658 18.9698 16.2104 18.9915C15.36 19.0061 14.9349 19.0134 14.5972 19.2098C14.2595 19.4062 14.0492 19.7614 13.6288 20.4718ZM8 11.75C7.58579 11.75 7.25 12.0858 7.25 12.5C7.25 12.9142 7.58579 13.25 8 13.25H13.5C13.9142 13.25 14.25 12.9142 14.25 12.5C14.25 12.0858 13.9142 11.75 13.5 11.75H8ZM7.25 9C7.25 8.58579 7.58579 8.25 8 8.25H16C16.4142 8.25 16.75 8.58579 16.75 9C16.75 9.41421 16.4142 9.75 16 9.75H8C7.58579 9.75 7.25 9.41421 7.25 9Z" fill="#ffffff"></path>
                                    </g>
                                </svg>
                                <span>Chat</span>
                                ` : g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                    <g id="SVGRepo_iconCarrier">
                                        <path d="M13.0867 21.3877L13.7321 21.7697L13.0867 21.3877ZM13.6288 20.4718L12.9833 20.0898L13.6288 20.4718ZM10.3712 20.4718L9.72579 20.8539H9.72579L10.3712 20.4718ZM10.9133 21.3877L11.5587 21.0057L10.9133 21.3877ZM2.3806 15.9134L3.07351 15.6264V15.6264L2.3806 15.9134ZM7.78958 18.9915L7.77666 19.7413L7.78958 18.9915ZM5.08658 18.6194L4.79957 19.3123H4.79957L5.08658 18.6194ZM21.6194 15.9134L22.3123 16.2004V16.2004L21.6194 15.9134ZM16.2104 18.9915L16.1975 18.2416L16.2104 18.9915ZM18.9134 18.6194L19.2004 19.3123H19.2004L18.9134 18.6194ZM19.6125 2.7368L19.2206 3.37628L19.6125 2.7368ZM21.2632 4.38751L21.9027 3.99563V3.99563L21.2632 4.38751ZM4.38751 2.7368L3.99563 2.09732V2.09732L4.38751 2.7368ZM2.7368 4.38751L2.09732 3.99563H2.09732L2.7368 4.38751ZM9.40279 19.2098L9.77986 18.5615L9.77986 18.5615L9.40279 19.2098ZM13.7321 21.7697L14.2742 20.8539L12.9833 20.0898L12.4412 21.0057L13.7321 21.7697ZM9.72579 20.8539L10.2679 21.7697L11.5587 21.0057L11.0166 20.0898L9.72579 20.8539ZM12.4412 21.0057C12.2485 21.3313 11.7515 21.3313 11.5587 21.0057L10.2679 21.7697C11.0415 23.0767 12.9585 23.0767 13.7321 21.7697L12.4412 21.0057ZM10.5 2.75H13.5V1.25H10.5V2.75ZM21.25 10.5V11.5H22.75V10.5H21.25ZM2.75 11.5V10.5H1.25V11.5H2.75ZM1.25 11.5C1.25 12.6546 1.24959 13.5581 1.29931 14.2868C1.3495 15.0223 1.45323 15.6344 1.68769 16.2004L3.07351 15.6264C2.92737 15.2736 2.84081 14.8438 2.79584 14.1847C2.75041 13.5189 2.75 12.6751 2.75 11.5H1.25ZM7.8025 18.2416C6.54706 18.2199 5.88923 18.1401 5.37359 17.9265L4.79957 19.3123C5.60454 19.6457 6.52138 19.7197 7.77666 19.7413L7.8025 18.2416ZM1.68769 16.2004C2.27128 17.6093 3.39066 18.7287 4.79957 19.3123L5.3736 17.9265C4.33223 17.4951 3.50486 16.6678 3.07351 15.6264L1.68769 16.2004ZM21.25 11.5C21.25 12.6751 21.2496 13.5189 21.2042 14.1847C21.1592 14.8438 21.0726 15.2736 20.9265 15.6264L22.3123 16.2004C22.5468 15.6344 22.6505 15.0223 22.7007 14.2868C22.7504 13.5581 22.75 12.6546 22.75 11.5H21.25ZM16.2233 19.7413C17.4786 19.7197 18.3955 19.6457 19.2004 19.3123L18.6264 17.9265C18.1108 18.1401 17.4529 18.2199 16.1975 18.2416L16.2233 19.7413ZM20.9265 15.6264C20.4951 16.6678 19.6678 17.4951 18.6264 17.9265L19.2004 19.3123C20.6093 18.7287 21.7287 17.6093 22.3123 16.2004L20.9265 15.6264ZM13.5 2.75C15.1512 2.75 16.337 2.75079 17.2619 2.83873C18.1757 2.92561 18.7571 3.09223 19.2206 3.37628L20.0044 2.09732C19.2655 1.64457 18.4274 1.44279 17.4039 1.34547C16.3915 1.24921 15.1222 1.25 13.5 1.25V2.75ZM22.75 10.5C22.75 8.87781 22.7508 7.6085 22.6545 6.59611C22.5572 5.57256 22.3554 4.73445 21.9027 3.99563L20.6237 4.77938C20.9078 5.24291 21.0744 5.82434 21.1613 6.73809C21.2492 7.663 21.25 8.84876 21.25 10.5H22.75ZM19.2206 3.37628C19.7925 3.72672 20.2733 4.20752 20.6237 4.77938L21.9027 3.99563C21.4286 3.22194 20.7781 2.57144 20.0044 2.09732L19.2206 3.37628ZM10.5 1.25C8.87781 1.25 7.6085 1.24921 6.59611 1.34547C5.57256 1.44279 4.73445 1.64457 3.99563 2.09732L4.77938 3.37628C5.24291 3.09223 5.82434 2.92561 6.73809 2.83873C7.663 2.75079 8.84876 2.75 10.5 2.75V1.25ZM2.75 10.5C2.75 8.84876 2.75079 7.663 2.83873 6.73809C2.92561 5.82434 3.09223 5.24291 3.37628 4.77938L2.09732 3.99563C1.64457 4.73445 1.44279 5.57256 1.34547 6.59611C1.24921 7.6085 1.25 8.87781 1.25 10.5H2.75ZM3.99563 2.09732C3.22194 2.57144 2.57144 3.22194 2.09732 3.99563L3.37628 4.77938C3.72672 4.20752 4.20752 3.72672 4.77938 3.37628L3.99563 2.09732ZM11.0166 20.0898C10.8136 19.7468 10.6354 19.4441 10.4621 19.2063C10.2795 18.9559 10.0702 18.7304 9.77986 18.5615L9.02572 19.8582C9.07313 19.8857 9.13772 19.936 9.24985 20.0898C9.37122 20.2564 9.50835 20.4865 9.72579 20.8539L11.0166 20.0898ZM7.77666 19.7413C8.21575 19.7489 8.49387 19.7545 8.70588 19.7779C8.90399 19.7999 8.98078 19.832 9.02572 19.8582L9.77986 18.5615C9.4871 18.3912 9.18246 18.3215 8.87097 18.287C8.57339 18.2541 8.21375 18.2487 7.8025 18.2416L7.77666 19.7413ZM14.2742 20.8539C14.4916 20.4865 14.6287 20.2564 14.7501 20.0898C14.8622 19.936 14.9268 19.8857 14.9742 19.8582L14.2201 18.5615C13.9298 18.7304 13.7204 18.9559 13.5379 19.2063C13.3646 19.4441 13.1864 19.7468 12.9833 20.0898L14.2742 20.8539ZM16.1975 18.2416C15.7862 18.2487 15.4266 18.2541 15.129 18.287C14.8175 18.3215 14.5129 18.3912 14.2201 18.5615L14.9742 19.8582C15.0192 19.832 15.096 19.7999 15.2941 19.7779C15.5061 19.7545 15.7842 19.7489 16.2233 19.7413L16.1975 18.2416Z" fill="#ffffff"></path>
                                        <path d="M8 9H16" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"></path>
                                        <path d="M8 12.5H13.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"></path>
                                    </g>
                                </svg>
                                <span>Chat</span>
                            `}
                    </button>
                </li>
                <li class="action-list-item">
                    <button class="action-btn" @click=${this.onFullscreen.bind(this)} id="fullscreen-btn">
                        ${this.fullscreen ? g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                                    <g id="SVGRepo_iconCarrier"> <path d="M21.7071 3.70711L16.4142 9H20C20.5523 9 21 9.44772 21 10C21 10.5523 20.5523 11 20 11H14.0007L13.997 11C13.743 10.9992 13.4892 10.9023 13.295 10.7092L13.2908 10.705C13.196 10.6096 13.1243 10.4999 13.0759 10.3828C13.0273 10.2657 13.0004 10.1375 13 10.003L13 10V4C13 3.44772 13.4477 3 14 3C14.5523 3 15 3.44772 15 4V7.58579L20.2929 2.29289C20.6834 1.90237 21.3166 1.90237 21.7071 2.29289C22.0976 2.68342 22.0976 3.31658 21.7071 3.70711Z" fill="#ffffff"/> <path d="M9 20C9 20.5523 9.44772 21 10 21C10.5523 21 11 20.5523 11 20V14.0007C11 13.9997 11 13.998 11 13.997C10.9992 13.7231 10.8883 13.4752 10.7092 13.295C10.7078 13.2936 10.7064 13.2922 10.705 13.2908C10.6096 13.196 10.4999 13.1243 10.3828 13.0759C10.2657 13.0273 10.1375 13.0004 10.003 13C10.002 13 10.001 13 10 13H4C3.44772 13 3 13.4477 3 14C3 14.5523 3.44772 15 4 15H7.58579L2.29289 20.2929C1.90237 20.6834 1.90237 21.3166 2.29289 21.7071C2.68342 22.0976 3.31658 22.0976 3.70711 21.7071L9 16.4142V20Z" fill="#ffffff"/> </g>
                                </svg>
                                <span>Exit Fullscreen</span>
                            ` : g`
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                                    <g id="SVGRepo_iconCarrier"> <path d="M21.7092 2.29502C21.8041 2.3904 21.8757 2.50014 21.9241 2.61722C21.9727 2.73425 21.9996 2.8625 22 2.997L22 3V9C22 9.55228 21.5523 10 21 10C20.4477 10 20 9.55228 20 9V5.41421L14.7071 10.7071C14.3166 11.0976 13.6834 11.0976 13.2929 10.7071C12.9024 10.3166 12.9024 9.68342 13.2929 9.29289L18.5858 4H15C14.4477 4 14 3.55228 14 3C14 2.44772 14.4477 2 15 2H20.9998C21.2749 2 21.5242 2.11106 21.705 2.29078L21.7092 2.29502Z" fill="#ffffff"/> <path d="M10.7071 14.7071L5.41421 20H9C9.55228 20 10 20.4477 10 21C10 21.5523 9.55228 22 9 22H3.00069L2.997 22C2.74301 21.9992 2.48924 21.9023 2.29502 21.7092L2.29078 21.705C2.19595 21.6096 2.12432 21.4999 2.07588 21.3828C2.02699 21.2649 2 21.1356 2 21V15C2 14.4477 2.44772 14 3 14C3.55228 14 4 14.4477 4 15V18.5858L9.29289 13.2929C9.68342 12.9024 10.3166 12.9024 10.7071 13.2929C11.0976 13.6834 11.0976 14.3166 10.7071 14.7071Z" fill="#ffffff"/> </g>
                                </svg>
                                <span>Fullscreen</span>
                            `}
                    </button>
                </li>
                <li class="action-list-item">
                    <button class="action-btn" id="leave-btn" @click=${this.onLeave.bind(this)}>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                            <g id="SVGRepo_iconCarrier"> <path d="M8 12.8617H7.25L7.25 12.8617L8 12.8617ZM8 13.4782H8.75L8.75 13.4782L8 13.4782ZM6.57997 15.3295L6.36905 14.6097H6.36905L6.57997 15.3295ZM4.57997 15.9156L4.36905 15.1959H4.36905L4.57997 15.9156ZM2 14.0643L2.75 14.0643L2 14.0643ZM2 12.1414H2.75H2ZM2.44083 10.7889L1.86331 10.3104H1.86331L2.44083 10.7889ZM12 7V7.75V7ZM21.3703 9.85601L21.8699 9.29661V9.29661L21.3703 9.85601ZM16 13.25H15.25H16ZM17.7004 15.1625L17.5916 15.9046L17.7004 15.1625ZM19.7004 15.4556L19.8091 14.7135L19.7004 15.4556ZM22 13.5429H21.25V13.5429L22 13.5429ZM22 11.4183H22.75V11.4183L22 11.4183ZM12 11.3963V10.6463V11.3963ZM7.25 12.8617L7.25 13.4782L8.75 13.4782L8.75 12.8617L7.25 12.8617ZM6.36905 14.6097L4.36905 15.1959L4.7909 16.6353L6.7909 16.0492L6.36905 14.6097ZM2.75 14.0643L2.75 12.1414H1.25L1.25 14.0643L2.75 14.0643ZM3.01834 11.2674C3.90179 10.2012 6.51318 7.75 12 7.75V6.25C5.98964 6.25 2.97874 8.96419 1.86331 10.3104L3.01834 11.2674ZM12 7.75C17.233 7.75 19.8648 9.51708 20.8708 10.4154L21.8699 9.29661C20.5944 8.15756 17.6087 6.25 12 6.25V7.75ZM15.25 12.8617V13.25H16.75V12.8617H15.25ZM17.5916 15.9046L19.5916 16.1977L19.8091 14.7135L17.8091 14.4204L17.5916 15.9046ZM22.75 13.5429V11.4183H21.25V13.5429H22.75ZM16 12.8617C16.75 12.8617 16.75 12.8607 16.75 12.8597C16.75 12.8593 16.75 12.8583 16.75 12.8576C16.75 12.8562 16.75 12.8548 16.75 12.8534C16.7499 12.8505 16.7499 12.8475 16.7498 12.8444C16.7497 12.8382 16.7495 12.8316 16.7492 12.8245C16.7485 12.8104 16.7475 12.7946 16.7459 12.7773C16.7428 12.7426 16.7374 12.7016 16.7283 12.6552C16.7102 12.5622 16.6776 12.4484 16.6202 12.3222C16.503 12.0645 16.2941 11.7811 15.9433 11.524C15.2573 11.0214 14.0694 10.6463 12 10.6463V12.1463C13.9306 12.1463 14.7427 12.5039 15.0567 12.734C15.2059 12.8433 15.247 12.9262 15.2548 12.9433C15.2599 12.9544 15.2586 12.9551 15.256 12.9423C15.2548 12.936 15.2533 12.9264 15.2521 12.9132C15.2515 12.9066 15.251 12.8991 15.2506 12.8905C15.2504 12.8862 15.2503 12.8817 15.2502 12.8769C15.2501 12.8745 15.2501 12.872 15.25 12.8695C15.25 12.8682 15.25 12.867 15.25 12.8657C15.25 12.865 15.25 12.864 15.25 12.8637C15.25 12.8627 15.25 12.8617 16 12.8617ZM12 10.6463C9.93056 10.6463 8.74267 11.0214 8.05672 11.524C7.7059 11.7811 7.49703 12.0645 7.37982 12.3222C7.32239 12.4484 7.2898 12.5622 7.27167 12.6552C7.26263 12.7016 7.25722 12.7426 7.25406 12.7773C7.25248 12.7946 7.25147 12.8104 7.25085 12.8245C7.25054 12.8316 7.25032 12.8382 7.25019 12.8444C7.25012 12.8475 7.25008 12.8505 7.25004 12.8534C7.25003 12.8548 7.25002 12.8562 7.25001 12.8576C7.25001 12.8583 7.25 12.8594 7.25 12.8597C7.25 12.8607 7.25 12.8617 8 12.8617C8.75 12.8617 8.75 12.8627 8.75 12.8637C8.75 12.864 8.74999 12.865 8.74999 12.8657C8.74998 12.867 8.74997 12.8682 8.74996 12.8695C8.74993 12.872 8.74989 12.8745 8.74984 12.8769C8.74973 12.8817 8.74958 12.8862 8.7494 12.8905C8.74902 12.8991 8.74849 12.9066 8.74789 12.9132C8.74669 12.9264 8.74518 12.936 8.74395 12.9423C8.74145 12.9551 8.74011 12.9544 8.74518 12.9433C8.75297 12.9262 8.7941 12.8433 8.94328 12.734C9.25733 12.5039 10.0694 12.1463 12 12.1463V10.6463ZM19.5916 16.1977C21.2257 16.4371 22.75 15.2128 22.75 13.5429L21.25 13.5429C21.25 14.2406 20.5953 14.8287 19.8091 14.7135L19.5916 16.1977ZM20.8708 10.4154C21.1133 10.632 21.25 10.9778 21.25 11.4183L22.75 11.4183C22.75 10.6848 22.5191 9.87638 21.8699 9.29661L20.8708 10.4154ZM2.75 12.1414C2.75 11.7741 2.84345 11.4785 3.01834 11.2674L1.86331 10.3104C1.40595 10.8624 1.25 11.5309 1.25 12.1414H2.75ZM15.25 13.25C15.25 14.5971 16.2642 15.71 17.5916 15.9046L17.8091 14.4204C17.1811 14.3284 16.75 13.8157 16.75 13.25L15.25 13.25ZM4.36905 15.1959C3.5319 15.4412 2.75 14.8281 2.75 14.0643L1.25 14.0643C1.25 15.8966 3.05932 17.1428 4.7909 16.6353L4.36905 15.1959ZM7.25 13.4782C7.25 13.9833 6.90669 14.4522 6.36905 14.6097L6.7909 16.0492C7.93988 15.7125 8.75 14.6777 8.75 13.4782L7.25 13.4782Z" fill="#ffffff"/> </g>
                        </svg>
                        <span>Leave</span>
                    </button>
                </li>
            </ul>
        `;
  }
};
O.styles = at`
      :host {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: var(--maia-videochat-toolbar-background, #62637F);
        color: white;
        padding: 0 0 0 10px;
        box-sizing: border-box;
      }
      
      ul {
        all: unset;
      }
      
      li {
        all: unset;
        height: 100%;
      }
      
      button {
        all: unset;
        cursor: pointer;
      }

      @media only screen and (max-width: 600px) {
        #fullscreen-btn {
          display: none;
        }
      }
      
      #actions-list {
       height: 100%; 
        display: flex;
      }
      
      .action-list-item {
        height: 100%;
        display: flex;
        align-items: center;
      }
      
      .action-btn {
        height: 100%; 
        display: flex;
        font-size: 10px;
        flex-direction: column;
        padding: 0 12px;
        align-items: center;
        justify-content: center;
        gap: 4px;
        
        & svg {
          width: 25px;
          height: 25px;
        }
        
        &:hover {
          background-color: #505168;
        }
        
        &:disabled {
          color: #ACBDBA;
          cursor: not-allowed;
          
          & svg {
            filter: invert(78%) sepia(8%) saturate(315%) hue-rotate(120deg) brightness(96%) contrast(85%);
          }
        }
      }
      
      #logo {
        height: 60%;
      }
      
      #leave-btn {
        background-color: #EF233C;
        
        &:hover {
          background-color: #D80032;
        }
      }
      
      #sound-btn {
        &:disabled {
          color: #ACBDBA;
          cursor: not-allowed;
          
          & svg {
            filter: invert(78%) sepia(8%) saturate(315%) hue-rotate(120deg) brightness(96%) contrast(85%);
          }
        }
      }
    `;
j([
  $({ type: Boolean, attribute: "mic-muted" })
], O.prototype, "micMuted", 2);
j([
  $({ type: Boolean, attribute: "sound-muted" })
], O.prototype, "soundMuted", 2);
j([
  $({ type: Boolean, attribute: "sound-disabled" })
], O.prototype, "soundDisabled", 2);
j([
  $({ type: Boolean })
], O.prototype, "fullscreen", 2);
j([
  $({ type: Boolean })
], O.prototype, "raised", 2);
j([
  $({ type: Boolean, attribute: "chat-open" })
], O.prototype, "chatOpen", 2);
j([
  $({ type: Boolean, attribute: "raise-disabled" })
], O.prototype, "raiseDisabled", 2);
O = j([
  rt("actions-toolbar")
], O);
var fo = Object.defineProperty, bo = Object.getOwnPropertyDescriptor, y = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? bo(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && fo(t, e, o), o;
};
let w = class extends R {
  constructor() {
    super(...arguments), this.maiaCognitive = !1, this.presentation = !1, this.loading = !1, this.recording = !1, this.avatars = [], this.backgrounds = [], this.currentIndex = -1, this.idle = !1, this.micMuted = !1, this.soundMuted = ao(), this.raised = !1, this.fullscreen = !1, this.chatOpen = !1, this.messages = [], this.conversationID = ei(), this.raiseCurrentIndex = -1, this.idleAudio = ct(), this.idleVideo = ct(), this.messageListRef = ct(), this.chatInputRef = ct(), this.displayWrapperRef = ct(), this.leaving = !1, this.stagingMessages = [];
  }
  connectedCallback() {
    super.connectedCallback(), console.assert(this.rasaUrl != null), console.assert(this.idleAvatarUrl != null), this.rasa = new no(this.rasaUrl), this.conversationID = ei(), this.leaving = !1, this.maiaCognitive ? this.startMAIACognitive() : this.start();
  }
  start() {
    navigator.mediaDevices.getUserMedia({ audio: !0 }).then((i) => {
      this.speechEvents = Ki(i, {
        threshold: -50
      });
      let t = !1;
      this.speechEvents.on("speaking", () => {
        t = !0, console.log("Started speaking");
      }), this.speechEvents.on("stopped_speaking", () => {
        this.speechEvents.suspend().catch(console.error), setTimeout(() => {
          var n;
          t = !1, (n = this.mediaRecorder) == null || n.stop();
        }, 500), console.log("Stopped speaking");
      }), this.mediaRecorder = new MediaRecorder(i);
      let e = [];
      const s = new ro(3), o = [];
      return this.mediaRecorder.addEventListener("dataavailable", (n) => {
        n.data.size != 0 && (e.length == 0 && e.push(n.data), t ? o.push(n.data) : o.length == 0 && s.push(n.data));
      }), this.mediaRecorder.addEventListener("start", () => {
        setTimeout(() => {
          this.recording = !0, this.idleAudio.value && (this.idleAudio.value.currentTime = 0, this.idleAudio.value.play().catch(console.error)), this.speechEvents.resume().catch(console.error);
        }, 300);
      }), this.mediaRecorder.addEventListener("stop", () => {
        var d;
        this.recording = !1;
        const n = [...e, ...s.get(), ...o], r = new Blob(n, {
          type: (d = this.mediaRecorder) == null ? void 0 : d.mimeType
        });
        this.loading = !0, this.leaving || this.rasa.sendAudio(r, this.conversationID).then((a) => (this.loading = !1, this.onRasaResponse(a.response, a.transcription), this.playVideos())).catch(console.error), s.reset(), o.length = 0, e.length = 0;
      }), this.speechEvents.suspend().catch(console.error), this.presentation ? this.rasa.sendText("/EXTERNAL_introduction", this.conversationID) : this.rasa.sendText("/EXTERNAL_welcome", this.conversationID);
    }).then((i) => (this.onRasaResponse(i), this.playVideos())).then(() => {
      this.dispatchEvent(new CustomEvent("ready"));
    }).catch((i) => {
      console.error(i);
    });
  }
  startMAIACognitive() {
    this.rasa.sendText("/EXTERNAL_introduction_maia_cognitive", this.conversationID).then((i) => (this.onRasaResponse(i), this.playVideos())).then(() => {
      this.dispatchEvent(new CustomEvent("ready"));
    }).catch((i) => {
      console.error(i);
    });
  }
  onRasaResponse(i, t = null) {
    this.idle = !1, this.currentIndex = 0, this.stagingMessages = [], t != null && this.messages.push({
      isUser: !0,
      message: {
        text: t
      }
    });
    for (let e of i)
      this.stagingMessages.push({
        isUser: !1,
        message: {
          text: e.custom.text
        }
      });
    this.messages.push(this.stagingMessages[this.currentIndex]), this.requestUpdate(), this.avatars = i.map((e) => e.custom.avatar), this.backgrounds = i.map((e) => e.custom.background);
  }
  onEndEventVideo() {
    var i;
    this.currentIndex >= this.avatars.length - 1 ? (this.idle = !0, this.currentIndex = -1, this.raised = !1, this.maiaCognitive || (i = this.mediaRecorder) == null || i.start(200)) : this.raised ? (this.idle = !0, this.raiseCurrentIndex = this.currentIndex, this.currentIndex = -1) : (this.idle = !1, this.currentIndex += 1, this.messages.push(this.stagingMessages[this.currentIndex]), this.requestUpdate()), this.playVideos().catch(console.error);
  }
  async playVideos() {
    var e, s;
    await this.updateComplete;
    const i = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".avatar.active");
    i != null && (i.currentTime = 0, i.muted = this.soundMuted, await i.play());
    const t = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".background.video.active");
    t != null && await t.play();
  }
  onSkip() {
    var e, s;
    const i = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".avatar.active");
    i != null && i.pause();
    const t = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".background.video.active");
    t != null && t.pause(), this.onEndEventVideo();
  }
  onMic() {
    var i, t, e, s;
    this.micMuted ? ((i = this.speechEvents) == null || i.resume().catch(console.error), (t = this.mediaRecorder) == null || t.stream.getAudioTracks().forEach((o) => {
      o.enabled = !0;
    })) : ((e = this.speechEvents) == null || e.suspend().catch(console.error), (s = this.mediaRecorder) == null || s.stream.getAudioTracks().forEach((o) => {
      o.enabled = !1;
    })), this.micMuted = !this.micMuted;
  }
  onRaise() {
    this.raised ? (this.raised = !1, this.currentIndex = this.raiseCurrentIndex, this.onEndEventVideo()) : this.raised = !0;
  }
  onFullscreen() {
    var i;
    this.fullscreen ? document.exitFullscreen().catch(console.error) : (i = this.shadowRoot) == null || i.host.requestFullscreen().catch(console.error), this.fullscreen = !this.fullscreen;
  }
  onLeave() {
    var i, t, e;
    this.loading = !1, this.recording = !1, this.avatars = [], this.backgrounds = [], this.currentIndex = -1, this.idle = !0, this.micMuted = !1, this.raised = !1, this.leaving = !0, this.fullscreen = !1, (i = this.speechEvents) == null || i.stop(), (t = this.mediaRecorder) == null || t.stop(), (e = this.mediaRecorder) == null || e.stream.getTracks().forEach((s) => s.stop()), this.dispatchEvent(new CustomEvent("leave"));
  }
  onSound() {
    var t;
    const i = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".avatar.active");
    i != null && (i.muted = !this.soundMuted), this.idleAudio.value != null && (this.idleAudio.value.muted = !this.soundMuted), this.soundMuted = !this.soundMuted;
  }
  onChat() {
    this.chatOpen = !this.chatOpen;
  }
  onChatInput(i) {
    this.messages.push({
      isUser: !0,
      message: {
        text: i.detail.message,
        datetime: i.detail.datetime
      }
    }), this.requestUpdate(), this.rasa.sendText(i.detail.message, this.conversationID).then((t) => (this.loading = !1, this.onRasaResponse(t), this.playVideos())).catch(console.error);
  }
  render() {
    return g`
            ${this.maiaCognitive ? g`
                        <button @click=${this.onSound} id="maia-cognitive-sound-btn">
                            ${this.soundMuted ? g`
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                        <g id="SVGRepo_iconCarrier"><path d="M16 9L22 15M22 9L16 15M13 3L7 8H5C3.89543 8 3 8.89543 3 10V14C3 15.1046 3.89543 16 5 16H7L13 21V3Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></g>
                                    </svg>       
                                ` : g`
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                                        <g id="SVGRepo_iconCarrier">
                                            <path
                                                d="M16 9C16.5 9.5 17 10.5 17 12C17 13.5 16.5 14.5 16 15M19 6C20.5 7.5 21 10 21 12C21 14 20.5 16.5 19 18M13 3L7 8H5C3.89543 8 3 8.89543 3 10V14C3 15.1046 3.89543 16 5 16H7L13 21V3Z"
                                                stroke="#000000"
                                                stroke-width="1.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></path>
                                        </g>
                                    </svg>
                                `}
                        </button>
                    ` : g`
                        <div id="actions-toolbar">
                            <actions-toolbar
                                    @chat=${this.onChat.bind(this)}
                                    @mic=${this.onMic.bind(this)}
                                    @raise=${this.onRaise.bind(this)}
                                    @fullscreen=${this.onFullscreen.bind(this)}
                                    @leave=${this.onLeave.bind(this)}
                                    @sound=${this.onSound.bind(this)}
                                    ?mic-muted=${this.micMuted}
                                    ?sound-muted=${this.soundMuted}
                                    ?raised=${this.raised}
                                    ?raise-disabled=${this.recording}
                                    ?fullscreen=${this.fullscreen}
                                    ?sound-disabled=${this.idle}
                                    ?chat-open=${this.chatOpen}
                            ></actions-toolbar>
                        </div>
                    `}
            <div id="display-wrapper" ${pt(this.displayWrapperRef)}>
                <div id="content-wrapper" class=${k({ "chat-open": this.chatOpen })}>
                    ${this.avatars.map((i, t) => {
      const e = t === this.currentIndex, s = this.backgrounds[this.currentIndex] || {};
      return g`
                            <div class=${k({
        "avatar-wrapper": !0,
        "with-video": s.video != null,
        "with-image": s.image != null
      })}>
                                <video
                                        src=${i}
                                        preload="auto"
                                        @ended=${this.onEndEventVideo.bind(this)}
                                        class=${k({ avatar: !0, active: e })}
                                        playsinline
                                        muted
                                ></video>
                            </div>
                        `;
    })}

                    <div class="avatar-wrapper">
                        <div id="idle-avatar-spinner"
                             class=${k({ recording: this.recording, loading: this.loading })}></div>
                        <video
                                src=${this.idleAvatarUrl}
                                preload="auto"
                                class=${k({ avatar: !0, active: this.idle })}
                                loop
                                playsinline
                                ?muted=${this.soundMuted}
                                ${pt(this.idleVideo)}
                        ></video>
                    </div>

                    <audio
                            src="https://storage.googleapis.com/maia-videochat/wakeup.wav"
                            preload="auto"
                            ?muted=${this.soundMuted}
                            ${pt(this.idleAudio)}
                    ></audio>

                    ${this.backgrounds.map((i, t) => {
      const e = t == this.currentIndex;
      if (i == null)
        return null;
      if (i.image != null)
        return g`
                                <div class=${k({ "background-wrapper": !0 })}>
                                    <img
                                            class=${k({ background: !0, image: !0, active: e })}
                                            src=${i.image}
                                            alt="Background image"
                                    >
                                </div>
                            `;
      if (i.video != null)
        return g`
                                <div class=${k({ "background-wrapper": !0 })}>
                                    <video
                                            class=${k({ background: !0, video: !0, active: e })}
                                            muted
                                            src=${i.video}
                                            playsinline
                                    ></video>
                                </div>
                            `;
      throw new Error(`Unsupported background: ${i}`);
    })}
                    ${this.idle ? null : g`
                                <button id="skip" @click=${this.onSkip.bind(this)}>
                                    <span>Skip</span>
                                    <svg width="800px" height="800px" viewBox="0 0 24 24"
                                         xmlns="http://www.w3.org/2000/svg" fill="none">
                                        <path stroke="#000000" stroke-linecap="round" stroke-linejoin="round"
                                              stroke-width="2"
                                              d="M19 5.5v13m-3.48-5.636-9.016 5.259A1 1 0 0 1 5 17.259V6.741a1 1 0 0 1 1.504-.864l9.015 5.26a1 1 0 0 1 0 1.727Z"/>
                                    </svg>
                                </button>
                            `}
                </div>
                <div id="chat-wrapper" class=${k({ "chat-open": this.chatOpen })}>
                    <div id="chat-message-list" ${pt(this.messageListRef)}>
                        <maia-message-list
                                messages=${JSON.stringify(this.messages)}
                        ></maia-message-list>
                    </div>
                    <div id="chat-input" ${pt(this.chatInputRef)}>
                        <maia-chat-input
                                @user-message=${this.onChatInput.bind(this)}
                        ></maia-chat-input>
                    </div>
                </div>
            </div>
        `;
  }
};
w.styles = at`
      :host {
        width: 960px;
        height: 540px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
        background: var(--maia-videochat-background, linear-gradient(270deg, #505168 0%, #27233A 100%));
      }

      #display-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
      }

      #content-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
        position: relative;

        &.chat-open {
          width: 70%;
        }
      }

      #loading {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .avatar-wrapper {
        position: absolute;
        width: 40%;
        aspect-ratio: 1 / 1;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 100;
        transition: left 1s linear, width 1s linear, height 1s linear, bottom 1s linear, transform 1s linear;
      }

      @media only screen and (min-width: 960px) {
        .avatar-wrapper {
          &.with-video {
            width: 20%;
            aspect-ratio: 1 / 1;
            top: auto;
            bottom: 10px;
            left: 10px;
            transform: none;
          }

          &.with-image {
            left: 25%;
          }
        }
      }

      .avatar {
        opacity: 0;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        transition: opacity 1s ease;

        &.active {
          opacity: 1;
        }
      }

      #idle-avatar-spinner {
        position: absolute;
        top: -4px;
        right: -4px;
        width: 100%;
        height: 100%;
        border: 4px solid transparent;
        border-radius: 50%;

        &.recording {
          border: 4px solid #4B71AA;
        }

        &.loading {
          border-top-color: #4B71AA;
          animation: spin 1s linear infinite;
        }
      }

      #idle-avatar {

      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .background-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      .background {
        opacity: 0;
        transition: opacity 1s ease;

        &.active {
          opacity: 1;
        }

        &.video {
          width: 100%;
          height: 100%;
        }

        &.image {
          width: 100%;
          height: 100%;
        }
      }

      #skip {
        all: unset;
        position: absolute;
        background-color: #CCEADE;
        color: #262626;
        padding: 10px 20px;
        bottom: 10px;
        right: 10px;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 6px;
        box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1);

        & svg {
          width: 20px;
          height: 20px;
        }
      }

      #actions-toolbar {
        display: flex;
        height: 50px;
      }

      #chat-wrapper {
        background-color: white;
        width: 0;
        // border-right: 2px solid #27233A;
        display: flex;
        flex-direction: column;
        height: 100%;

        &.chat-open {
          width: 30%;
        }
      }

      #chat-message-list {
        display: flex;
        width: 100%;
        height: calc(100% - 69px)
      }

      #chat-input {
        width: 100%;
      }

      @media only screen and (max-width: 960px) {
        :host {
          height: 100vh;
          width: 100vw;
          height: -webkit-fill-available;
        }

        .avatar-wrapper {
          width: 75%;
          max-width: unset;
          height: unset;
          max-height: 75%;
        }

        #content-wrapper {
          height: calc(100vh - 50px);
        }

        .background {
          display: none;
        }
      }

      @media only screen and (max-height: 540px) {
        .avatar-wrapper {
          height: 75%;
          max-height: unset;
          max-width: 75%;
          width: unset;
        }
      }
      
      #maia-cognitive-sound-btn {
        all: unset;
        z-index: 100;
        cursor: pointer;
        box-sizing: border-box;
        position: absolute;
        padding: 10px;
        top: 20px;
        left: 20px;
        width: 50px;
        height: 50px;
        background-color: #CCEADE;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        border-radius: 10px;
      }
    `;
y([
  $({ attribute: "rasa-url" })
], w.prototype, "rasaUrl", 2);
y([
  $({ type: Boolean, attribute: "maia-cognitive" })
], w.prototype, "maiaCognitive", 2);
y([
  $({ attribute: "idle-avatar-url" })
], w.prototype, "idleAvatarUrl", 2);
y([
  $({ type: Boolean })
], w.prototype, "presentation", 2);
y([
  x()
], w.prototype, "loading", 2);
y([
  x()
], w.prototype, "recording", 2);
y([
  x()
], w.prototype, "avatars", 2);
y([
  x()
], w.prototype, "backgrounds", 2);
y([
  x()
], w.prototype, "currentIndex", 2);
y([
  x()
], w.prototype, "idle", 2);
y([
  x()
], w.prototype, "micMuted", 2);
y([
  x()
], w.prototype, "soundMuted", 2);
y([
  x()
], w.prototype, "raised", 2);
y([
  x()
], w.prototype, "fullscreen", 2);
y([
  x()
], w.prototype, "chatOpen", 2);
y([
  x()
], w.prototype, "messages", 2);
w = y([
  rt("maia-videochat-display")
], w);
var wo = Object.defineProperty, Co = Object.getOwnPropertyDescriptor, lt = (i, t, e, s) => {
  for (var o = s > 1 ? void 0 : s ? Co(t, e) : t, n = i.length - 1, r; n >= 0; n--)
    (r = i[n]) && (o = (s ? r(t, e, o) : r(o)) || o);
  return s && o && wo(t, e, o), o;
};
let P = class extends R {
  constructor() {
    super(...arguments), this.started = !1, this.loading = !1, this.presentation = !1;
  }
  onStartCall(i) {
    this.loading = !0, this.started = !0, this.presentation = i.detail.presentation;
  }
  onLeave() {
    this.started = !1, this.loading = !1;
  }
  onReady() {
    this.started = !0, this.loading = !1;
  }
  render() {
    return this.started ? g`
                ${this.loading ? g`
                        <videochat-calling
                            @leave=${this.onLeave.bind(this)}
                        ></maia-videochat-loading>` : null}
                <div id="videochat-display" class=${k({ hidden: this.loading })}>
                    <maia-videochat-display
                        ?presentation=${this.presentation}
                        @ready=${this.onReady.bind(this)}
                        @leave=${this.onLeave.bind(this)}
                        rasa-url=${this.rasaUrl}
                        idle-avatar-url=${this.idleAvatarUrl}
                    ></maia-videochat-display>
                </div>
            ` : g`
                <maia-videochat-start
                    @call=${this.onStartCall.bind(this)}
                ></maia-videochat-start>
            `;
  }
};
P.styles = at`
      .hidden {
        display: none;
      }
    `;
lt([
  $({ attribute: "rasa-url" })
], P.prototype, "rasaUrl", 2);
lt([
  $({ attribute: "idle-avatar-url" })
], P.prototype, "idleAvatarUrl", 2);
lt([
  x()
], P.prototype, "started", 2);
lt([
  x()
], P.prototype, "loading", 2);
lt([
  x()
], P.prototype, "presentation", 2);
P = lt([
  rt("maia-videochat")
], P);
